import ExpirationDateField from "./ExpirationDateField";
import { expirationDatePayload } from "../../utils/expirationDate";
import { OCR_POPUP_STYLES } from "./ocrPopupStyles";
import { purchaseSupplierError } from "./documentPopupMessages";
import { activePurchaseSuppliers } from "../../utils/purchaseSuppliers";
import { supplierIdentityIds } from "../../utils/supplierIdentity";
import { deliveryNoteDuplicateError } from "./deliveryNoteLifecycleMessages";
import OcrDuplicateResolution, { OcrDuplicateSuccess } from "./OcrDuplicateResolution";
import OcrReopenReviewButton, { isCreatedOcrImport } from "./OcrReopenReviewButton";
import { ocrReopenText } from "./ocrReopenReviewMessages";
import DeliveryNoteAdjustments from "./DeliveryNoteAdjustments";
import { prepareDeliveryNoteAdjustments, removeAppliedDiscounts, repairAppliedDiscountBindings } from "../../utils/deliveryNoteAdjustments";
/**
 * Pilcore - Delivery note OCR inbox.
 * Lists scanned documents, reads them, and opens the review flow.
 */

import AddItemPopup from "./AddItemPopup";
import OcrSupplierPickerModal from "./OcrSupplierPickerModal";
import OcrAiReviewPanel from "./OcrAiReviewPanel";
import OcrAiReviewComments from "./OcrAiReviewComments";
import OcrDistributorPanel from "./OcrDistributorPanel";
import { ocrDistributorTranslator } from "./ocrDistributorMessages";
import { filterResolvedOcrReviewFlags } from "../../utils/ocrReviewQuality";
import { buildSuggestedInternalCode, findReliableOcrItemForLine } from "../../utils/ocrItemSuggestions";
import { completePendingOcrAiItemMatch } from "../../utils/ocrAiReview";
import { hasOcrAiUnitProposal, matchOcrUnitLevel, readOcrUnitEvidence, restoreOcrUnitEvidence } from "../../utils/ocrUnitMatching";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaEye,
  FaFileAlt,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTimes,
  FaTrash,
  FaUpload,
} from "react-icons/fa";
import useManagementDialog from "../../hooks/useManagementDialog";
import useOcrBusinessUnitScope, { OCR_SCOPE_CHANGED, isOcrScopeError } from "../../hooks/useOcrBusinessUnitScope";
import useOcrReviewDraft from "../../hooks/useOcrReviewDraft";
import { hasOcrPermission, useOcrBackgroundUploads } from "../OcrBackgroundUploads";
import OcrUploadQueue from "./OcrUploadQueue";
import { ocrReviewDraftTranslator } from "./ocrReviewDraftMessages";
import { readStoredUser } from "../../../utils/sessionUser";
import {
  buildDeliveryNoteUnitOptions,
  defaultDeliveryNoteUnitLevel,
  deliveryNoteInventoryPreview,
  deliveryNoteInventoryValues,
  formatUnitNumber,
  isDeliveryNoteUnitLevelValid,
  unitHierarchyLabel,
} from "../../utils/deliveryNoteUnits";
import { useReceivingPopupUi } from "./receivingPopupMessages";
import OriginalDocumentLayout, { useOriginalDocument } from "../originals/OriginalDocumentLayout";
import { useOriginalDocumentDialogScope } from "../originals/OriginalDocumentDialogScope";
import { getUnitName } from "../../utils/unitNames";
import ComplimentaryPriceField from "./ComplimentaryPriceField";
import { useComplimentaryConfirmation } from "./ComplimentaryStockConfirmation";
import { fetchDeliveryNoteReferencePrice } from "../../utils/deliveryNoteReferencePrice";
import { useDocumentDialog } from "./PurchasingDocumentDialog";
import { isExplicitZeroPrice, normalizeComplimentaryPrice, updateComplimentaryLine } from "../../utils/complimentaryStock";
import "../../PurchasingUi.css";
import "./PurchasingReceivingPopups.css";

const OCR_UPLOAD_ACCEPT = "application/pdf,.pdf,image/*,.jpg,.jpeg,.png,.tif,.tiff,.heic,.heif";
const supplierMatchesId = (supplier, id) => supplierIdentityIds(supplier).includes(Number(id));

const OCR_STATUS_META = {
  pending: { label: "Pending", className: "text-bg-warning", tone: "warning" },
  reviewed: { label: "Reviewed", className: "text-bg-info", tone: "info" },
  confirmed: { label: "Confirmed", className: "text-bg-success", tone: "success" },
  discarded: { label: "Discarded", className: "text-bg-secondary", tone: "muted" },
  uploaded: { label: "Uploaded", className: "text-bg-primary", tone: "info" },
  processing: { label: "Processing", className: "text-bg-info", tone: "info" },
  needs_review: { label: "Needs review", className: "text-bg-warning", tone: "warning" },
  ready: { label: "Ready", className: "text-bg-success", tone: "success" },
  created: { label: "Created", className: "text-bg-success", tone: "success" },
  failed: { label: "Failed", className: "text-bg-danger", tone: "danger" },
  duplicate: { label: "Duplicate", className: "text-bg-secondary", tone: "muted" },
};

const OCR_STATUS_FILTERS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "needs_review", label: "Needs review" },
  { value: "ready", label: "Ready" },
  { value: "uploaded", label: "Uploaded" },
  { value: "processing", label: "Processing" },
  { value: "created", label: "Created" },
  { value: "failed", label: "Failed" },
];

// The invoice inbox uses the same OCR presentation.


function statusMeta(status) {
  return OCR_STATUS_META[status] || {
    label: status || "Unknown",
    className: "text-bg-secondary",
    tone: "muted",
  };
}

function statusClassName(status) {
  return `ocrp-status ocrp-status--${statusMeta(status).tone || "muted"}`;
}

function sourceLabel(source) {
  if (!source) return "—";
  return String(source)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatCurrency(value) {
  if (value == null || value === "") return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";

  return `${parsed.toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

function formatFileSize(bytes) {
  const parsed = Number(bytes);
  if (!Number.isFinite(parsed) || parsed <= 0) return "—";
  if (parsed < 1024 * 1024) return `${(parsed / 1024).toFixed(0)} KB`;
  return `${(parsed / 1024 / 1024).toFixed(1)} MB`;
}

function compactDocumentLabel(value, maxLength = 10) {
  const text = String(value || "").trim();
  if (!text) return "—";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, Math.max(maxLength - 1, 1))}…`;
}

function parseImportData(ocrImport) {
  const raw = ocrImport?.normalized_json || ocrImport?.ocr_json || {};
  if (!raw) return {};

  try {
    return typeof raw === "string" ? JSON.parse(raw) : raw;
  } catch (err) {
    console.error("[OCR][POPUP][ERROR] Parsing import JSON:", err);
    return {};
  }
}

// Reviewed OCR stores allocation positions, but intentionally does not carry
// delivery-note database IDs into a new review. Rebuild local identities only
// when the saved position still refers to the same article.
export function hydrateOcrReviewAdjustments(parsed = {}) {
  parsed = parsed && typeof parsed === "object" ? parsed : {};
  const reviewed = Number.isSafeInteger(Number(parsed.metadata?.review?.created_delivery_note_id))
    && Number(parsed.metadata.review.created_delivery_note_id) > 0;
  const lines = (Array.isArray(parsed.lines) ? parsed.lines : []).map((line, index) => ({
    ...(reviewed ? { adjustment_key: `reviewed-ocr-line-${index}` } : {}),
    description: line.description || "",
    ocr_description: line.ocr_description || line.description || "",
    supplier_code: extractLineSupplierCode(line),
    quantity: line.quantity ?? "",
    unit_price: line.unit_price ?? "",
    expiration_date: line.expiration_date || null,
    price_status: normalizeComplimentaryPrice(line, "unit_price").price_status,
    price_estimate_source: normalizeComplimentaryPrice(line, "unit_price").price_estimate_source || null,
    is_free_stock: Boolean(line.is_free_stock) && isExplicitZeroPrice(line.unit_price),
    free_stock_reason: line.is_free_stock && isExplicitZeroPrice(line.unit_price) ? line.free_stock_reason || "" : "",
    unit_level: line.unit_level || "",
    ...readOcrUnitEvidence(line),
    suggested_item_id: resolveSuggestedItemId(line),
    suggested_item: line.suggested_item || null,
    po_receipts: line.po_receipts || [],
  }));
  let adjustmentHydrationError = false;
  const expenses = (Array.isArray(parsed.expenses) ? parsed.expenses : []).map((expense, expenseIndex) => {
    const entry = { description: String(expense?.description || ""), amount: expense?.amount ?? "", distribute_cost: false,
      ...(expense?.discount_type === "percentage" ? { discount_type: "percentage", percentage: expense.percentage ?? "" } : {}) };
    if (!reviewed || !expense?.distribute_cost) return entry;
    entry.distribute_cost = true;
    if (!expense.allocation) return entry; // Legacy proportional expenses have no snapshot.
    if (!Array.isArray(expense.allocation.entries) || !expense.allocation.entries.length) {
      adjustmentHydrationError = true;
      return entry;
    }
    const seen = new Set();
    entry.allocation = { ...expense.allocation, entries: expense.allocation.entries.map((part, partIndex) => {
      const index = part?.line_index;
      const line = Number.isInteger(index) && index >= 0 ? lines[index] : null;
      if (!line || !part?.item_id || String(line.suggested_item_id) !== String(part.item_id) || seen.has(index)) {
        adjustmentHydrationError = true;
        return { ...part, line_id: null, key: `unresolved-ocr-adjustment-${expenseIndex}-${partIndex}` };
      }
      seen.add(index);
      // Older reviewed JSON omitted free-stock flags, although its saved
      // distribution can prove that this article was fully discounted.
      if (Number(part.before) > 0 && Number(part.amount) < 0 && Number(part.after) === 0
        && Number(part.quantity) === Number(line.quantity) && isExplicitZeroPrice(line.unit_price)) {
        line.is_free_stock = true; line.price_status = "confirmed"; line.free_stock_reason = "Fully discounted";
      }
      return { ...part, line_id: null, key: line.adjustment_key, line_index: index };
    }) };
    return entry;
  });
  return { lines, expenses, adjustmentHydrationError };
}

function hasReviewData(ocrImport) {
  return Boolean(ocrImport?.ocr_json || ocrImport?.normalized_json || (ocrImport?.s3_key && ocrImport?.status === "failed"));
}

function createdDeliveryNoteId(ocrImport) {
  const value = ocrImport?.created_delivery_note_id;
  if (typeof value !== "number" && typeof value !== "string") return null;
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
}

function detectedLineCount(ocrImport) {
  const parsed = parseImportData(ocrImport);
  return Array.isArray(parsed.lines) ? parsed.lines.length : 0;
}

function qualityFlags(ocrImport) {
  const parsed = parseImportData(ocrImport);
  const flags = parsed?.metadata?.quality?.flags;
  return Array.isArray(flags) ? flags : [];
}

function qualitySummary(ocrImport) {
  const flags = qualityFlags(ocrImport);
  if (!flags.length) return null;

  const reviewFlags = flags.filter((flag) => flag.requires_review || flag.severity === "error");
  const visibleFlags = reviewFlags.length ? reviewFlags : flags;
  const first = visibleFlags[0]?.message || visibleFlags[0]?.code || "OCR quality check";
  const extraCount = Math.max(visibleFlags.length - 1, 0);

  return {
    count: visibleFlags.length,
    hasReviewFlags: reviewFlags.length > 0,
    text: extraCount ? `${first} +${extraCount}` : first,
    title: visibleFlags.map((flag) => flag.message || flag.code).join("\n"),
  };
}

function canReadDocument(ocrImport) {
  return Boolean(ocrImport?.s3_key) && ["pending", "uploaded", "processing", "failed"].includes(ocrImport?.status);
}

function hasQualityFlag(flags, code) {
  return flags.some((flag) => flag.code === code);
}

function toNumber(value) {
  if (value == null || value === "") return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function isPositiveNumber(value) {
  const parsed = toNumber(value);
  return parsed != null && parsed > 0;
}


function normalizeLookup(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function resolveSuggestedItemId(line) {
  const value = line?.suggested_item_id ?? line?.item_id ?? line?.matched_item_id ?? line?.suggested_item?.id;
  return value == null || value === "" ? "" : String(value);
}

function pickFirstText(values) {
  for (const value of values || []) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      const nested = pickFirstText(value);
      if (nested) return nested;
      continue;
    }
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
}

function pickObjectValue(source, keys) {
  if (!source || typeof source !== "object") return "";
  const entries = Object.entries(source);
  for (const key of keys || []) {
    const normalizedKey = normalizeLookup(key).replace(/[^a-z0-9]+/g, "");
    const match = entries.find(([entryKey]) => normalizeLookup(entryKey).replace(/[^a-z0-9]+/g, "") === normalizedKey);
    const value = match ? match[1] : undefined;
    const text = pickFirstText([value]);
    if (text) return text;
  }
  return "";
}

function extractScannedSupplierProfile(parsedImport, importData) {
  const header = parsedImport?.header || {};
  const supplier = parsedImport?.supplier || {};
  const metadata = parsedImport?.metadata || {};
  const sources = [header, supplier, metadata, parsedImport, importData].filter(Boolean);
  const pick = (keys) => pickFirstText(sources.map((source) => pickObjectValue(source, keys)));

  return {
    name: pickFirstText([
      importData?.supplier_guess,
      importData?.suggested_supplier_name,
      header.supplier,
      supplier.name,
      pick(["supplier", "supplier_name", "vendor_name", "merchant_name", "name"]),
    ]),
    supplier_id: pick(["supplier_code", "supplier_id", "vendor_id", "merchant_id"]),
    tax_id: pick(["supplier_tax_id", "tax_id", "vat_number", "vat_registration_number", "nif", "cif"]),
    phone: pick(["supplier_phone", "phone", "telephone", "vendor_phone"]),
    email: pick(["supplier_email", "email", "vendor_email"]),
    address: pick(["supplier_address", "address", "vendor_address", "merchant_address"]),
    contact_name: pick(["contact_name", "contact", "supplier_contact"]),
  };
}

function extractLineSupplierCode(line) {
  const fields = line?.textract_fields || {};
  const candidates = [
    line?.supplier_code,
    line?.item_supplier_code,
    line?.supplier_item_code,
    line?.product_code,
    line?.item_code,
    line?.sku,
    line?.reference,
    line?.ref,
    pickObjectValue(fields, ["PRODUCT_CODE", "ITEM_CODE", "SKU", "PART_NUMBER", "REFERENCE", "REF", "CODE"]),
  ];
  return pickFirstText(candidates);
}

function sortByName(rows = []) {
  return [...rows].sort((left, right) =>
    String(left?.name || "").localeCompare(String(right?.name || ""), "en", { sensitivity: "base" })
  );
}

function selectedItemLabel(item, fallback = "Choose or create item") {
  if (!item) return fallback;
  return [item.name, item.code].filter(Boolean).join(" · ") || fallback;
}

function formatQty(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";
  return formatUnitNumber(parsed, 8);
}

function pendingQtyFromPo(line) {
  return Number(line?.pending_quantity ?? line?.quantity_pending ?? 0) || 0;
}

function assignmentQty(line) {
  return (line?.po_receipts || []).reduce((sum, receipt) => sum + (Number(receipt?.quantity_received ?? receipt?.quantity) || 0), 0);
}

function shouldShowReadButton(ocrImport) {
  return canReadDocument(ocrImport);
}

function readButtonLabel(ocrImport, readingImportId) {
  if (readingImportId === ocrImport?.id) {
    return "Reading...";
  }

  if (ocrImport?.status === "processing") {
    return "Check result";
  }

  if (ocrImport?.status === "failed") {
    return "Read again";
  }

  return "Read document";
}

export default function OcrOptionsPopup({ isOpen, onClose, onDeleted, onCreated, onViewDeliveryNote }) {
  const { t, themeId } = useReceivingPopupUi();
  const [ocrList, setOcrList] = useState([]);
  const [loading, setLoading] = useState(false);
  const originalViewer = useOriginalDocument(isOpen);
  const [readingImportId, setReadingImportId] = useState(null);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [selected, setSelected] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletingImportId, setDeletingImportId] = useState(null);
  const [deleteError, setDeleteError] = useState("");
  const deleteInFlight = React.useRef(null);
  const deletedImportIds = React.useRef(new Set());
  const activeReadIds = React.useRef(new Set());
  const { api: scopedApi, businessUnitId, user: currentUser, blocked: scopeBlocked } = useOcrBusinessUnitScope(isOpen, () => {
    setOcrList([]); setSelected(null); setDeleteTarget(null); setMessage(""); setError("");
    setReadingImportId(null); originalViewer.close();
  });
  const canViewImports = hasOcrPermission(currentUser, "view_tab");
  const canCreateImports = hasOcrPermission(currentUser, "create");
  const canDeleteImports = hasOcrPermission(currentUser, "delete");
  const [, refreshPermissions] = useState(0);

  useEffect(() => {
    const refresh = () => refreshPermissions((version) => version + 1);
    window.addEventListener("storage", refresh);
    window.addEventListener("palantir-session-updated", refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener("palantir-session-updated", refresh);
    };
  }, []);

  useEffect(() => {
    if (!canViewImports) { setOcrList([]); setMessage(""); setError(""); }
    if (!canViewImports || !canCreateImports) setSelected(null);
    if (!canViewImports || !canDeleteImports) setDeleteTarget(null);
  }, [canViewImports, canCreateImports, canDeleteImports]);

  const summary = useMemo(() => {
    return (ocrList || []).reduce(
      (acc, item) => {
        acc.total += 1;
        if (item.status === "needs_review") acc.needsReview += 1;
        if (item.status === "ready") acc.ready += 1;
        if (item.status === "processing") acc.processing += 1;
        if (item.status === "created") acc.created += 1;
        return acc;
      },
      { total: 0, needsReview: 0, ready: 0, processing: 0, created: 0 }
    );
  }, [ocrList]);

  const visibleImports = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return (ocrList || []).filter((item) => {
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (!query) return true;

      return [
        item.id,
        item.status,
        item.supplier_guess,
        item.suggested_supplier_name,
        item.delivery_number_guess,
        item.delivery_date_guess,
        item.original_file_name,
        item.source,
      ]
        .filter((value) => value != null)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [ocrList, searchTerm, statusFilter]);

  const fetchOcrImports = useCallback(async ({ silent = false } = {}) => {
    if (!scopedApi.isCurrent() || !canViewImports || !hasOcrPermission(readStoredUser(), "view_tab")) return;
    if (!silent) setLoading(true);
    setError("");
    try {
      const res = await scopedApi.get("/api/purchasing/ocr-imports", {
        params: { status: "all" },
      });
      if (!hasOcrPermission(readStoredUser(), "view_tab")) return;
      setOcrList((res.data || []).filter((item) => !deletedImportIds.current.has(item.id)));
    } catch (err) {
      if (isOcrScopeError(err)) return;
      console.error("[OCR][LIST][ERROR]", err);
      setError("Error loading OCR imports.");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [scopedApi, canViewImports]);

  const backgroundUploads = useOcrBackgroundUploads();
  const uploadQueue = backgroundUploads?.queue;
  const canUpload = canCreateImports && Boolean(uploadQueue);
  const queueRows = uploadQueue?.rows;
  const queuedImportIds = useMemo(() => new Set((queueRows || [])
    .filter(row => row.importId && ["queued", "uploading", "reading"].includes(row.status))
    .map(row => Number(row.importId))), [queueRows]);
  const uploadRevision = backgroundUploads?.revision;
  const refreshedUploadRevision = React.useRef(uploadRevision);

  // ✅ Hook siempre al nivel superior
  useEffect(() => {
    if (isOpen) fetchOcrImports();
  }, [fetchOcrImports, isOpen]);

  useEffect(() => {
    if (refreshedUploadRevision.current === uploadRevision) return;
    refreshedUploadRevision.current = uploadRevision;
    if (isOpen) fetchOcrImports({ silent: true });
  }, [fetchOcrImports, isOpen, uploadRevision]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const hasProcessingRows = ocrList.some((item) => ["uploaded", "processing"].includes(item.status));
    if (!hasProcessingRows && !readingImportId) return undefined;

    const timer = window.setInterval(() => {
      fetchOcrImports({ silent: true });
    }, 5000);

    return () => window.clearInterval(timer);
  }, [fetchOcrImports, isOpen, ocrList, readingImportId]);

  useEffect(() => {
    if (!readingImportId || deleteInFlight.current === readingImportId || deletedImportIds.current.has(readingImportId)) return;

    const current = ocrList.find((item) => item.id === readingImportId);
    if (current && !["pending", "uploaded", "processing"].includes(current.status)) {
      if (current.status === "ready" || current.status === "needs_review") {
        setMessage(t("Document #{id} read successfully ({count} lines).", { id: current.id, count: detectedLineCount(current) }));
      } else if (current.status === "failed") {
        setError(t("Document #{id} could not be read{error}", { id: current.id, error: current.error_message ? `: ${current.error_message}` : "." }));
      }
      setReadingImportId(null);
    }
  }, [ocrList, readingImportId, t]);


  // ✅ Nuevo sistema basado en input real con ref
  const fileInputRef = React.useRef(null);

  const handleScanNew = () => {
    if (!scopedApi.isCurrent() || !hasOcrPermission(readStoredUser(), "create")) return;
    // Clic directo sobre un input real existente en el DOM
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!canUpload || !scopedApi.isCurrent() || !files.length) return;
    setError("");
    setMessage("");
    uploadQueue.enqueue(files);
  };

  const handleViewOriginal = (ocrImport, event) => {
    event?.stopPropagation();
    if (!ocrImport?.id || !hasOcrPermission(readStoredUser(), "view_tab")) return;
    originalViewer.open(ocrImport.id, ocrImport.original_file_name);
  };

  const readDocument = async (ocrImport, { auto = false } = {}) => {
    if (!ocrImport?.id || !hasOcrPermission(readStoredUser(), "create")) return;
    if (queuedImportIds.has(Number(ocrImport.id)) || deleteInFlight.current || deletedImportIds.current.has(ocrImport.id) || activeReadIds.current.has(ocrImport.id) || (!auto && !canReadDocument(ocrImport))) return;
    activeReadIds.current.add(ocrImport.id);

    try {
      setReadingImportId(ocrImport.id);
      setError("");
      setMessage(
        auto
          ? t("Document #{id} uploaded. Reading automatically...", { id: ocrImport.id })
          : t("Reading document #{id}...", { id: ocrImport.id })
      );

      const res = await scopedApi.post(`/api/purchasing/ocr-imports/${ocrImport.id}/process`, {
        wait: true,
        force: ocrImport.status === "failed",
        max_wait_ms: 180000,
        poll_interval_ms: 3000,
      });
      if (deletedImportIds.current.has(ocrImport.id) || deleteInFlight.current === ocrImport.id) return;

      const nextStatus = res.data?.import?.status || (res.data?.completed ? "ready" : "processing");
      const lineCount = res.data?.line_count || res.data?.import?.line_count || 0;

      if (res.data?.timed_out) {
        setMessage(t("Document #{id} is still being read. This list updates automatically.", { id: ocrImport.id }));
      } else if (nextStatus === "ready" || nextStatus === "needs_review") {
        setMessage(t("Document #{id} read successfully ({count} lines).", { id: ocrImport.id, count: lineCount }));
      } else {
        setMessage(t("Document #{id} status: {status}.", { id: ocrImport.id, status: t(statusMeta(nextStatus).label) }));
      }

      await fetchOcrImports();
    } catch (err) {
      if (isOcrScopeError(err)) return;
      if (deletedImportIds.current.has(ocrImport.id) || deleteInFlight.current === ocrImport.id) return;
      console.error("[OCR][READ][ERROR]", err);
      setError(t("Error reading document: ") + (err.response?.data?.error || err.message));
      await fetchOcrImports();
    } finally {
      activeReadIds.current.delete(ocrImport.id);
      setReadingImportId((value) => value === ocrImport.id ? null : value);
    }
  };

  const handleReadDocument = async (ocrImport, event) => {
    event?.stopPropagation();
    await readDocument(ocrImport);
  };

  const handleOpenReview = (ocrImport, event) => {
    event?.stopPropagation();
    if (!scopedApi.isCurrent() || !hasOcrPermission(readStoredUser(), "view_tab") || queuedImportIds.has(Number(ocrImport.id)) ||
        !hasOcrPermission(readStoredUser(), "create") || deleteInFlight.current ||
        deletedImportIds.current.has(ocrImport.id) || !hasReviewData(ocrImport)) return;
    setSelected(ocrImport);
  };

  const canViewDeliveryNote = (ocrImport) => canViewImports &&
    createdDeliveryNoteId(ocrImport) !== null && typeof onViewDeliveryNote === "function";

  const handleViewDeliveryNote = (ocrImport, event) => {
    event?.stopPropagation();
    if (!scopedApi.isCurrent() || !hasOcrPermission(readStoredUser(), "view_tab") ||
        deleteInFlight.current || deletedImportIds.current.has(ocrImport.id) ||
        !canViewDeliveryNote(ocrImport)) return;
    onViewDeliveryNote(createdDeliveryNoteId(ocrImport));
  };

  const canOpenImport = (ocrImport) => isCreatedOcrImport(ocrImport)
    ? canViewDeliveryNote(ocrImport)
    : canCreateImports && !queuedImportIds.has(Number(ocrImport.id)) && hasReviewData(ocrImport);

  const handleOpenImport = (ocrImport) => {
    if (isCreatedOcrImport(ocrImport)) handleViewDeliveryNote(ocrImport);
    else handleOpenReview(ocrImport);
  };

  const requestDelete = (ocrImport, event) => {
    event?.stopPropagation();
    if (queuedImportIds.has(Number(ocrImport.id)) || !hasOcrPermission(readStoredUser(), "delete") || ocrImport.can_delete === false || deleteInFlight.current) return;
    setDeleteTarget(ocrImport);
    setDeleteError("");
    setError("");
    setMessage("");
  };

  const closeDelete = () => {
    if (!deleteInFlight.current) { setDeleteTarget(null); setDeleteError(""); }
  };

  const deleteImport = async () => {
    if (!deleteTarget || !hasOcrPermission(readStoredUser(), "delete") || deleteInFlight.current) return;
    const current = ocrList.find((item) => item.id === deleteTarget.id);
    if (!current || current.can_delete === false || queuedImportIds.has(Number(current.id))) return;
    deleteInFlight.current = current.id;
    setDeletingImportId(current.id);
    setDeleteError("");
    setMessage("");
    try {
      const { data } = await scopedApi.delete(`/api/purchasing/ocr-imports/${current.id}`);
      if (data?.success !== true) throw new Error("Deletion was not confirmed.");
      deletedImportIds.current.add(current.id);
      setReadingImportId((value) => value === current.id ? null : value);
      setOcrList((rows) => rows.filter((item) => item.id !== current.id));
      setSelected((value) => value?.id === current.id ? null : value);
      setDeleteTarget(null);
      setMessage(t("Import #{id} deleted.", { id: current.id }));
      await fetchOcrImports({ silent: true });
      await onDeleted?.(current.id);
    } catch (failure) {
      if (isOcrScopeError(failure)) return;
      const errors = {
        OCR_IMPORT_PROCESSING: "This document is being processed. Wait for it to finish before deleting it.",
        OCR_ORIGINAL_DELETE_FAILED: "The stored original file could not be deleted. The import has been kept; please try again.",
      };
      setDeleteError(t(errors[failure.response?.data?.code] || "Could not delete the import and its original file. Please try again."));
    } finally {
      deleteInFlight.current = null;
      setDeletingImportId(null);
    }
  };

  const handleReopened = (ocrImport) => {
    setOcrList((rows) => rows.map((row) => row.id === ocrImport.id ? ocrImport : row));
    setSelected(ocrImport);
  };

  const renderImportActions = (ocrImport) => {
    const alreadyCreated = isCreatedOcrImport(ocrImport);
    const inQueue = queuedImportIds.has(Number(ocrImport.id));
    const canReview = canCreateImports && hasReviewData(ocrImport);
    const hasOriginal = Boolean(ocrImport?.s3_key);
    const showRead = !alreadyCreated && canCreateImports && shouldShowReadButton(ocrImport);
    const isReading = readingImportId === ocrImport?.id || activeReadIds.current.has(ocrImport?.id);

    return (
      <div className="ocrp-importActions" onClick={(event) => event.stopPropagation()}>
        {showRead && (
          <button
            type="button"
            className={`ocrp-btn ocrp-btn--info ocrp-importAction--read${canReview ? " ocrp-importAction--secondary" : ""}`}
            onClick={(event) => handleReadDocument(ocrImport, event)}
            disabled={inQueue || deletingImportId != null || isReading || !canReadDocument(ocrImport)}
            title={t(readButtonLabel(ocrImport, readingImportId))}
          >
            <FaSyncAlt />
            <span>{t(readButtonLabel(ocrImport, readingImportId))}</span>
          </button>
        )}
        {hasOriginal && (
          <button
            type="button"
            className="ocrp-btn ocrp-btn--info ocrp-importAction--original"
            onClick={(event) => handleViewOriginal(ocrImport, event)}
            disabled={deletingImportId != null}
            title={t("View original")}
          >
            <FaEye />
            <span>{t("Original")}</span>
          </button>
        )}
        {canReview && (
          <button
            type="button"
            className={`ocrp-btn ocrp-importAction--review${alreadyCreated ? "" : " ocrp-btn--warning"}`}
            onClick={(event) => handleOpenReview(ocrImport, event)}
            disabled={inQueue || deletingImportId != null}
            title={t(alreadyCreated ? "View review" : "Review document")}
          >
            {alreadyCreated ? <FaEye /> : <FaCheckCircle />}
            <span>{t(alreadyCreated ? "View review" : "Review")}</span>
          </button>
        )}
        {canDeleteImports && ocrImport.can_delete !== false && <button
          type="button"
          className="ocrp-btn ocrp-btn--danger ocrp-importAction--delete"
          onClick={(event) => requestDelete(ocrImport, event)}
          disabled={inQueue || deletingImportId != null}
          aria-label={t("Delete import #{id}", { id: ocrImport.id })}
          title={t("Delete import #{id}", { id: ocrImport.id })}
        ><FaTrash aria-hidden="true" /></button>}
      </div>
    );
  };




  if (!isOpen) return null;
  if (scopeBlocked || !canViewImports) return <div className="ocrp-overlay pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}>
    <style>{OCR_POPUP_STYLES}</style>
    <section className="ocrp-modal" role="dialog" aria-modal="true" aria-label={t("Delivery Note Inbox")}>
      <div className="ocrp-body"><p className="ocrp-alert ocrp-alert--warning" role="alert">{t(scopeBlocked ? OCR_SCOPE_CHANGED : "You do not have permission to view the delivery note inbox.")}</p>
        <button type="button" className="ocrp-btn" onClick={onClose}>{t("Close")}</button></div>
    </section>
  </div>;


  return (
    <div className="ocrp-overlay pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}>
      <style>{OCR_POPUP_STYLES}</style>
      <OriginalDocumentLayout original={selected ? null : originalViewer.original} onClose={originalViewer.close}>
      <div className="ocrp-modal" role="dialog" aria-modal={!(originalViewer.original && !selected)} aria-label={t("Delivery Note Inbox")}>
        {canUpload && <input
          ref={fileInputRef}
          type="file"
          multiple
          aria-label={t("Upload documents")}
          accept={OCR_UPLOAD_ACCEPT}
          style={{ display: "none" }}
          onChange={handleFileSelected}
        />}

        <header className="ocrp-head">
          <div>
            <h2 className="ocrp-title">{t("Delivery Note Inbox")}</h2>
            <div className="ocrp-sub">{t(canCreateImports ? "Upload delivery notes or review those received via WhatsApp." : "You can view documents and originals. Uploading, processing and creating delivery notes requires the create permission.")}</div>
            {canUpload && <div className="ocrp-sub">{t("Select multiple files in one go. They will upload one at a time.")}</div>}
            <div className="ocrp-sub">
              {summary.total} {t("documents ·")} {summary.needsReview} {t("need review ·")} {summary.processing} {t("processing")} </div>
          </div>
          <div className="ocrp-headActions">
            {canUpload && <button
              type="button"
              className="ocrp-btn ocrp-btn--primary"
              onClick={handleScanNew}
              disabled={loading}
              title={t("Upload documents")}
            >
              <FaUpload />
              <span>{t("Upload documents")}</span>
            </button>}
            <button type="button" className={uploadQueue?.busy ? "ocrp-btn" : "ocrp-btn ocrp-btn--close"} onClick={onClose} disabled={deletingImportId != null} title={t("Close")}>
              <FaTimes aria-hidden="true" />{uploadQueue?.busy && t("Close")}
            </button>
          </div>
        </header>

        <main className="ocrp-body">
          {error && <div className="ocrp-alert ocrp-alert--error" role="alert">{t(error)}</div>}
          {message && <div className="ocrp-alert ocrp-alert--success" role="status">{t(message)}</div>}
          <OcrUploadQueue queue={uploadQueue || { rows: [] }} t={t} background
            onRefresh={() => fetchOcrImports()} refreshDisabled={loading} />

          <section className="ocrp-stats" aria-label={t("OCR import summary")}>
            <div className="ocrp-stat">
              <span>{t("Total")}</span>
              <strong>{summary.total}</strong>
            </div>
            <div className="ocrp-stat">
              <span>{t("Needs Review")}</span>
              <strong>{summary.needsReview}</strong>
            </div>
            <div className="ocrp-stat">
              <span>{t("Ready")}</span>
              <strong>{summary.ready}</strong>
            </div>
            <div className="ocrp-stat">
              <span>{t("Processing")}</span>
              <strong>{summary.processing}</strong>
            </div>
            <div className="ocrp-stat">
              <span>{t("Created")}</span>
              <strong>{summary.created}</strong>
            </div>
          </section>

          <section className="ocrp-toolbar" aria-label={t("OCR import filters")}>
            <div className="ocrp-search">
              <FaSearch />
              <input
                className="ocrp-input"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={t("Search supplier, number, file, source...")}
              />
            </div>
            <div className="ocrp-filterRow">
              {OCR_STATUS_FILTERS.map((option) => {
                const count =
                  option.value === "all"
                    ? ocrList.length
                    : ocrList.filter((item) => item.status === option.value).length;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`ocrp-chip ${statusFilter === option.value ? "ocrp-chip--active" : ""}`}
                    onClick={() => setStatusFilter(option.value)}
                    aria-pressed={statusFilter === option.value}
                  >
                    {t(option.label)}
                    <span>{count}</span>
                  </button>
                );
              })}
            </div>
          </section>

          {loading ? (
            <div className="ocrp-empty">
              <strong>{t("Loading OCR imports")}</strong>
              <span className="ocrp-small">{t("Preparing the document list.")}</span>
            </div>
          ) : visibleImports.length === 0 ? (
            <div className="ocrp-empty">
              <strong>{t("No documents found")}</strong>
              <span className="ocrp-small">{t("Adjust the filters or upload a new document.")}</span>
            </div>
          ) : (
            <>
              <div className="ocrp-tableShell">
                <table className="ocrp-table">
                  <colgroup>
                    <col className="ocrp-colImport" />
                    <col className="ocrp-colStatus" />
                    <col className="ocrp-colDocument" />
                    <col className="ocrp-colSupplier" />
                    <col className="ocrp-colDate" />
                    <col className="ocrp-colNumber" />
                    <col className="ocrp-colTotal" />
                    <col className="ocrp-colActions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>{t("Import")}</th>
                      <th>{t("Status")}</th>
                      <th>{t("Document")}</th>
                      <th>{t("Supplier")}</th>
                      <th>{t("Date")}</th>
                      <th>{t("Number")}</th>
                      <th style={{ textAlign: "right" }}>{t("Total")}</th>
                      <th style={{ textAlign: "right" }}>{t("Actions")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleImports.map((imp) => {
                      const canOpen = canOpenImport(imp);
                      const meta = statusMeta(imp.status);
                      const quality = qualitySummary(imp);
                      const documentLabel = imp.original_file_name || imp.delivery_number_guess || t("Import #{id}", { id: imp.id });

                      return (
                        <tr
                          key={imp.id}
                          style={{ cursor: canOpen ? "pointer" : "default" }}
                          onClick={() => handleOpenImport(imp)}
                        >
                          <td className="ocrp-strong">#{imp.id}</td>
                          <td>
                            <span className={statusClassName(imp.status)}>{t(meta.label)}</span>
                          </td>
                          <td>
                            <div className="ocrp-docTitle" title={documentLabel}>
                              <FaFileAlt />
                              <span>{compactDocumentLabel(documentLabel)}</span>
                            </div>
                            <div className="ocrp-muted ocrp-small">
                              {sourceLabel(imp.source)} · {formatFileSize(imp.file_size)} · {detectedLineCount(imp)} {t("lines")} </div>
                            {quality && (
                              <div
                                className={`ocrp-quality ${
                                  quality.hasReviewFlags ? "ocrp-quality--warning" : "ocrp-quality--info"
                                }`}
                                title={quality.title}
                              >
                                <FaExclamationTriangle />
                                <span>{quality.text}</span>
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="ocrp-supplierName" title={imp.suggested_supplier_name || imp.supplier_guess || ""}>
                              {imp.suggested_supplier_name || imp.supplier_guess || "—"}
                              {imp.suggested_supplier_name && parseImportData(imp)?.distributor?.name && <em className="ocrp-distributorName">{parseImportData(imp).distributor.name}</em>}
                            </div>
                          </td>
                          <td>{imp.delivery_date_guess || "—"}</td>
                          <td>{imp.delivery_number_guess || "—"}</td>
                          <td style={{ textAlign: "right" }}>{formatCurrency(imp.total_guess)}</td>
                          <td>{renderImportActions(imp)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="ocrp-mobileList">
                {visibleImports.map((imp) => {
                  const canOpen = canOpenImport(imp);
                  const meta = statusMeta(imp.status);
                  const quality = qualitySummary(imp);
                  const documentLabel = imp.delivery_number_guess || imp.original_file_name || t("Import #{id}", { id: imp.id });

                  return (
                    <article
                      key={`mobile-ocr-${imp.id}`}
                      className="ocrp-mobileCard"
                      style={{ cursor: canOpen ? "pointer" : "default" }}
                      onClick={() => handleOpenImport(imp)}
                    >
                      <div className="ocrp-mobileTop">
                        <div>
                          <div className="ocrp-mobileTitle">{documentLabel}</div>
                          <div className="ocrp-muted ocrp-small">
                            #{imp.id} · {sourceLabel(imp.source)} · {detectedLineCount(imp)} {t("lines")} </div>
                        </div>
                        <span className={statusClassName(imp.status)}>{t(meta.label)}</span>
                      </div>
                      <div className="ocrp-mobileGrid">
                        <div>
                          <div className="ocrp-mobileLabel">{t("Supplier")}</div>
                          <div className="ocrp-mobileValue">{imp.suggested_supplier_name || imp.supplier_guess || "—"}
                            {imp.suggested_supplier_name && parseImportData(imp)?.distributor?.name && <em className="ocrp-distributorName">{parseImportData(imp).distributor.name}</em>}
                          </div>
                        </div>
                        <div>
                          <div className="ocrp-mobileLabel">{t("Date")}</div>
                          <div className="ocrp-mobileValue">{imp.delivery_date_guess || "—"}</div>
                        </div>
                        <div>
                          <div className="ocrp-mobileLabel">{t("Total")}</div>
                          <div className="ocrp-mobileValue">{formatCurrency(imp.total_guess)}</div>
                        </div>
                        <div>
                          <div className="ocrp-mobileLabel">{t("File")}</div>
                          <div className="ocrp-mobileValue">{formatFileSize(imp.file_size)}</div>
                        </div>
                      </div>
                      {quality && (
                        <div
                          className={`ocrp-quality ${
                            quality.hasReviewFlags ? "ocrp-quality--warning" : "ocrp-quality--info"
                          }`}
                          title={quality.title}
                        >
                          <FaExclamationTriangle />
                          <span>{quality.text}</span>
                        </div>
                      )}
                      <div className="mt-2">{renderImportActions(imp)}</div>
                    </article>
                  );
                })}
              </div>
            </>
          )}
        </main>
      </div>
      </OriginalDocumentLayout>

      {canCreateImports && selected && (
        <OcrSavedReview
          key={`${selected.id}:${selected.status}:${selected.created_delivery_note_id || "review"}`}
          importData={selected}
          scopedApi={scopedApi}
          businessUnitId={businessUnitId}
          currentUser={currentUser}
          originalViewer={originalViewer}
          onViewOriginal={handleViewOriginal}
          onCreated={onCreated}
          onViewDeliveryNote={onViewDeliveryNote}
          onReopened={handleReopened}
          onClose={() => {
            setSelected(null);
            originalViewer.close();
            fetchOcrImports();
          }}
        />
      )}
      {deleteTarget && <DeleteOcrImportDialog ocrImport={deleteTarget} busy={deletingImportId != null} error={deleteError} onCancel={closeDelete} onConfirm={deleteImport} />}
    </div>
  );

}

function DeleteOcrImportDialog({ ocrImport, busy, error, onCancel, onConfirm }) {
  const { t } = useReceivingPopupUi();
  const dialogRef = useManagementDialog(onCancel, busy);
  return <div className="ocrp-reviewOverlay" onMouseDown={(event) => event.target === event.currentTarget && onCancel()}>
    <section className="ocrp-reviewModal ocrp-deleteModal" ref={dialogRef} role="alertdialog" aria-modal="true" aria-labelledby="ocr-delete-title" aria-describedby="ocr-delete-description" tabIndex={-1}>
      <header className="ocrp-reviewHead"><h3 id="ocr-delete-title" className="ocrp-title">{t("Delete import #{id}?", { id: ocrImport.id })}</h3></header>
      <div className="ocrp-reviewBody" id="ocr-delete-description">
        <strong>{ocrImport.original_file_name || ocrImport.delivery_number_guess || t("Import #{id}", { id: ocrImport.id })}</strong>
        <p>{t(ocrImport.s3_key ? "The import and its stored original file will be permanently deleted. This cannot be undone." : "The import will be permanently deleted. This cannot be undone.")}</p>
        <p>{t("Any delivery note already created from this import will be kept.")}</p>
        {error && <div className="ocrp-alert ocrp-alert--error" role="alert">{error}</div>}
      </div>
      <footer className="ocrp-reviewFooter"><button type="button" className="ocrp-btn" disabled={busy} onClick={onCancel}>{t("Cancel")}</button><button type="button" className="ocrp-btn ocrp-btn--danger" disabled={busy} onClick={onConfirm}>{busy ? t("Deleting...") : t("Delete permanently")}</button></footer>
    </section>
  </div>;
}


function OcrItemPickerModal({
  scopedApi,
  line,
  lineIndex,
  items,
  selectedItemId,
  selectedSupplierName,
  selectedSupplierId,
  onSelect,
  onAdded,
  onClose,
}) {
  const { t, themeId, languageId } = useReceivingPopupUi();
  const { isSplit } = useOriginalDocumentDialogScope();
  const [manualQuery, setManualQuery] = useState(null);
  const [mode, setMode] = useState("list");

  const initialDraft = useMemo(() => {
    const itemName = pickFirstText([line?.description, line?.ocr_description]);
    return {
      code: buildSuggestedInternalCode(line, items),
      supplier_code: extractLineSupplierCode(line),
      name: itemName,
      description: itemName,
      family_id: "",
      subfamily_id: "",
      category_id: "",
      subcategory_id: "",
      unit_id: "",
      pack_size: "",
    };
  }, [items, line]);

  const suggestion = Array.from(initialDraft.name.normalize("NFC")).slice(2, 5).join("").trim();
  const query = manualQuery ?? suggestion;
  const showSuggestion = manualQuery === null && Boolean(suggestion);

  const filteredItems = useMemo(() => {
    const normalizedQuery = normalizeLookup(query);
    if (!normalizedQuery) return items || [];
    return (items || []).filter((item) =>
      normalizeLookup([
        item?.code,
        item?.supplier_code,
        item?.name,
        item?.category?.name,
        item?.subcategory?.name,
        getUnitName(item?.unit, languageId),
      ].filter(Boolean).join(" ")).includes(normalizedQuery)
    );
  }, [items, query, languageId]);

  if (mode === "create") return <AddItemPopup isOpen initialItem={initialDraft} supplierIdLock={selectedSupplierId} requestClient={scopedApi}
    onClose={() => setMode("list")} onAdded={(createdItem) => onAdded(lineIndex, createdItem)} />;

  return (
    <div className="ocrp-pickerOverlay pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId} onMouseDown={(event) => event.target === event.currentTarget && onClose?.()}>
      <div className="ocrp-pickerModal ocrp-pickerModal--wide" onMouseDown={(event) => event.stopPropagation()} role="dialog" aria-modal={!isSplit} aria-label={t("Choose or create item")}>
        <div className="ocrp-pickerHead">
          <div>
            <h3>{t("Choose or create item")}</h3>
            <p>
              {t("Search supplier items for {supplier}, or create a new item from this OCR line.", { supplier: selectedSupplierName || t("the selected supplier") })}
            </p>
          </div>
          <button type="button" className="ocrp-btn" onClick={onClose}> {t("Close")} </button>
        </div>

        <div className="ocrp-pickerBody ocrp-pickerBody--items">
            <>
              <div className="ocrp-pickerIntro">
                <div>
                  <strong>{initialDraft.name || t("OCR line")}</strong>
                  <span>
                    {extractLineSupplierCode(line)
                      ? t("Supplier item code detected: {code}", { code: extractLineSupplierCode(line) })
                      : t("No supplier item code detected")}
                  </span>
                </div>
                <button type="button" className="ocrp-btn ocrp-btn--primary" onClick={() => setMode("create")}>
                  <FaPlus />
                  <span>{t("Create item")}</span>
                </button>
              </div>

              <div className="ocrp-search ocrp-itemSearch">
                <FaSearch />
                <input
                  className="ocrp-input"
                  type="search"
                  aria-describedby={showSuggestion ? "ocr-item-search-hint" : undefined}
                  value={query}
                  onChange={(event) => setManualQuery(event.target.value)}
                  placeholder={t("Search code, item, category, unit...")}
                />
                {showSuggestion && <span className="ocrp-searchHint">
                  <span aria-hidden="true">{query}</span>
                  <em id="ocr-item-search-hint">{t("Search suggestion. Clear to reset")}</em>
                </span>}
              </div>

              <div className="ocrp-pickerList">
                {filteredItems.length ? (
                  filteredItems.map((item) => (
                    <div key={item.id} className="ocrp-pickerRow">
                      <div>
                        <div className="ocrp-pickerRowTitle">{selectedItemLabel(item)}</div>
                        <div className="ocrp-pickerRowMeta">
                          {[item.supplier_code, item.category?.name, item.subcategory?.name, getUnitName(item.unit, languageId)]
                            .filter(Boolean)
                            .join(" · ") || t("No extra item data")}
                        </div>
                      </div>
                      <button
                        type="button"
                        className={`ocrp-btn ${String(selectedItemId) === String(item.id) ? "ocrp-btn--success" : "ocrp-btn--info"}`}
                        onClick={() => onSelect?.(lineIndex, item)}
                      >
                        {String(selectedItemId) === String(item.id) ? t("Selected") : t("Select")}
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="ocrp-empty">
                    <strong>{t("No items found")}</strong>
                    <span className="ocrp-small">{t("Create an item from this scanned line.")}</span>
                  </div>
                )}
              </div>
            </>

        </div>
      </div>
    </div>
  );
}

// ===============================
// 🔍 Popup de Detalle OCR
// ===============================
const OCR_COMPLIMENTARY_FIELDS = {
  priceKey: "unit_price",
  identityFields: ["suggested_item_id", "quantity", "unit_level"],
};
const OCR_ADJUSTMENT_OPTIONS = { quantityKey: "quantity", priceKey: "unit_price", getItemId: resolveSuggestedItemId };
function resetLineForSupplier(line, supplierId) {
  return {
    ...updateComplimentaryLine(line, "suggested_item_id", "", OCR_COMPLIMENTARY_FIELDS),
    suggested_item: null, unit_level: "", po_receipts: [], is_free_stock: false, free_stock_reason: "",
    _itemSelectionConfirmed: false, _ocrUnitSelectionManual: false,
    _aiPendingItemMatch: Boolean(supplierId && hasOcrAiUnitProposal(line)), _aiMatchSupplierId: supplierId,
  };
}
const ocrCommercialSignature = (lines) => JSON.stringify(lines.map((line) => [
  line.id ?? line.temp_id ?? line.adjustment_key ?? null, resolveSuggestedItemId(line),
  toNumber(line.quantity), toNumber(line.unit_price), line.unit_level || "",
  line.price_status || "", Boolean(line.is_free_stock), line.price_estimate_source || null,
]));
const ocrAdjustmentMessage = (message, languageId) => {
  const messages = {
    "Discount removed after editing items. Apply it again when you finish.": [
      "Se ha quitado el descuento al modificar los artículos. Vuelve a aplicarlo al terminar.",
      "S'ha tret el descompte en modificar els articles. Torna a aplicar-lo en acabar.",
    ],
    "Apply the percentage discount before saving.": ["Aplica el descuento porcentual antes de guardar.", "Aplica el descompte percentual abans de desar."],
    "Undo the distribution before removing or replacing an affected item.": [
      "El reparto del descuento ha quedado desactualizado. Deshaz el reparto y vuelve a aplicarlo.",
      "El repartiment del descompte ha quedat desactualitzat. Desfés-lo i torna a aplicar-lo.",
    ],
  };
  const language = String(languageId).split(/[-_]/)[0];
  return messages[message]?.[language === "es" ? 0 : language === "ca" ? 1 : -1] || message;
};

function OcrCreationSuccess({ createdId, learningFailed, onClose }) {
  const { t, themeId } = useReceivingPopupUi();
  const dialogRef = useManagementDialog(onClose, true);
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (learningFailed) return undefined;
    const timer = window.setTimeout(() => onCloseRef.current(), 1200);
    return () => window.clearTimeout(timer);
  }, [learningFailed]);

  return (
    <div className="ocrp-reviewOverlay pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}>
      <section className="ocrp-reviewModal ocrp-createdNotice" ref={dialogRef} role="status" aria-live="polite" aria-atomic="true" tabIndex={-1}>
        <FaCheckCircle aria-hidden="true" />
        <p>{t("Delivery note #{id} created successfully.", { id: createdId })}</p>
        {learningFailed && <>
          <p role="alert">{t("The delivery note was saved, but OCR could not save what it learned from your corrections.")}</p>
          <button type="button" className="ocrp-btn" onClick={onClose}>{t("Close")}</button>
        </>}
      </section>
    </div>
  );
}

function OcrSavedReview(props) {
  const { t, themeId, languageId } = useReceivingPopupUi();
  const alreadyCreated = isCreatedOcrImport(props.importData);
  const draftText = ocrReviewDraftTranslator(languageId);
  const recoveryConfirmation = useDocumentDialog();
  const draft = useOcrReviewDraft({
    importId: props.importData.id,
    businessUnitId: props.businessUnitId,
    userId: props.currentUser?.id,
    scopedApi: props.scopedApi,
    enabled: !props.importData.created_delivery_note_id &&
      !["created", "confirmed", "discarded", "processing"].includes(props.importData.status),
  });
  const [closing, setClosing] = useState(false);
  const [recovered, setRecovered] = useState(null);
  const editorImport = useMemo(() => ({ ...props.importData, ...draft.sourceImport }), [props.importData, draft.sourceImport]);
  const closeReview = async () => {
    if (closing) return;
    setClosing(true);
    const pending = draft.flush();
    // A durable browser backup lets the user leave immediately even when
    // the network is slow. Otherwise keep the editor open until saving works.
    if (draft.canLeave()) {
      props.onClose();
      return;
    }
    await pending;
    if (draft.canLeave()) props.onClose();
    else setClosing(false);
  };
  const viewDeliveryNote = async (id) => {
    if (closing || !props.scopedApi.isCurrent()) return;
    const pending = draft.flush();
    if (!draft.canLeave()) await pending;
    if (draft.canLeave() && props.scopedApi.isCurrent()) props.onViewDeliveryNote?.(id);
  };
  const recoverDraft = async () => {
    if (!await recoveryConfirmation.confirm(draftText("Replace the current draft with the changes kept on this browser?"))) return;
    const saved = await draft.recover();
    if (saved) setRecovered((previous) => ({ value: saved, key: (previous?.key || 0) + 1 }));
  };
  const useSavedDraft = async () => {
    const saved = await draft.useSaved();
    setRecovered((previous) => ({ value: saved, key: (previous?.key || 0) + 1 }));
  };
  const statuses = {
    loading: "Loading your draft…",
    idle: "Changes are saved automatically.",
    saving: "Saving draft…",
    saved: "Draft saved automatically",
    local: "Saved on this browser. Pending sync.",
    error: !draft.ready
      ? "Could not load your saved draft. Retry to continue."
      : draft.safeToLeave
      ? "The saved draft could not be loaded. Changes are kept on this browser until you retry."
      : "Could not save the draft. Keep this window open and retry.",
    conflict: "A newer draft exists. Your local changes have been kept separately.",
    blocked: "This import has changed. Close it and reopen it before continuing.",
  };
  const status = <div className="ocrp-sub" role="status" aria-live="polite">
    <span>{alreadyCreated
      ? ocrReopenText(languageId, "Review completed. Reopen it to make corrections.")
      : draftText(statuses[draft.status] || statuses.idle)}</span>
    {["local", "error", "conflict"].includes(draft.status) && <button type="button" className="ocrp-btn"
      onClick={draft.retry} disabled={closing}>{draftText("Retry sync")}</button>}
    {draft.hasRecovery && <button type="button" className="ocrp-btn"
      onClick={recoverDraft} disabled={closing}>{draftText("Recover my local changes")}</button>}
    {draft.hasRecovery && <button type="button" className="ocrp-btn"
      onClick={useSavedDraft} disabled={closing}>{draftText("Use saved draft")}</button>}
  </div>;

  // Mount the editor only once the stored values are known. Loading a draft
  // after edits begin could overwrite the user's new work.
  if (!draft.ready) return <div className="ocrp-reviewOverlay purchasing-receiving-ui" data-theme={themeId}>
    <section className="ocrp-reviewModal" role="dialog" aria-label={t("Review import {id}", { id: props.importData.id })}>
      <div className="ocrp-reviewBody">{status}</div>
      <footer className="ocrp-reviewFooter"><button type="button" className="ocrp-btn" onClick={props.onClose}>{t("Close")}</button></footer>
    </section>
  </div>;

  return <>
    <OcrDetailPopup {...props} importData={editorImport} key={recovered?.key || 0} onClose={closeReview}
      onViewDeliveryNote={props.onViewDeliveryNote ? viewDeliveryNote : undefined}
      initialDraft={recovered ? recovered.value : draft.initialDraft} onDraftChange={draft.onChange}
      onDraftComplete={draft.complete} draftStatus={status} closingDraft={closing}
      draftBlocked={alreadyCreated || draft.status === "blocked" || draft.status === "conflict"} />
    {recoveryConfirmation.dialog}
  </>;
}

function OcrDetailPopup({ importData, scopedApi, businessUnitId, currentUser, originalViewer, onViewOriginal, onClose, onCreated, onReopened, onViewDeliveryNote,
  initialDraft, onDraftChange, onDraftComplete, draftStatus, closingDraft, draftBlocked }) {
  const { t, themeId, languageId } = useReceivingPopupUi();
  const distributorText = ocrDistributorTranslator(languageId);
  const dateConfirmation = useDocumentDialog();
  const [saving, setSaving] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [duplicateRequest, setDuplicateRequest] = useState(null);
  const [updatedNote, setUpdatedNote] = useState(null);
  const [learningFailed, setLearningFailed] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSupplierName, setAiSupplierName] = useState(initialDraft?.aiSupplierName ?? null);
  const [aiSupplierEvidenceId, setAiSupplierEvidenceId] = useState(initialDraft?.aiSupplierEvidenceId ??
    (initialDraft?.aiSupplierName ? initialDraft.selectedSupplier || "" : ""));
  const [aiApplied, setAiApplied] = useState(initialDraft?.aiApplied ?? false);
  const [aiEvidence, setAiEvidence] = useState(initialDraft?.aiEvidence ?? { totals: null, warnings: [] });
  const [dateConfirmed, setDateConfirmed] = useState(initialDraft?.dateConfirmed ?? Boolean(importData?.delivery_date_guess));
  const aiReviewInFlight = React.useRef(false);
  const handleAiBusy = useCallback((value) => { aiReviewInFlight.current = value; setAiBusy(value); }, []);
  const saveInFlight = React.useRef(false);
  const mounted = React.useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
const parsedImport = useMemo(() => parseImportData(importData), [importData]);
const originalReviewFlags = useMemo(() => qualityFlags(importData), [importData]);
const qualityMetrics = parsedImport?.metadata?.quality?.metrics || {};

const initialReview = useMemo(() => hydrateOcrReviewAdjustments(parsedImport), [parsedImport]);
const [lines, setLines] = useState(() => initialDraft?.lines
  ? restoreOcrUnitEvidence(initialDraft.lines, initialReview.lines, { aiApplied: initialDraft.aiApplied })
  : initialReview.lines);
const [storedExpenses, setExpenses] = useState(initialDraft?.expenses ?? initialReview.expenses);
// Repair only provable, unchanged saved line bindings; never recalculate valid prices on opening.
const expenses = useMemo(() => repairAppliedDiscountBindings(lines, storedExpenses, OCR_ADJUSTMENT_OPTIONS), [lines, storedExpenses]);
const adjustmentStateRef = React.useRef({ lines, expenses });
adjustmentStateRef.current = { lines, expenses };
const [discountNotice, setDiscountNotice] = useState(0);
const [adjustmentEditError, setAdjustmentEditError] = useState("");
useEffect(() => {
  if (!discountNotice) return undefined;
  const timer = window.setTimeout(() => setDiscountNotice(0), 2500);
  return () => window.clearTimeout(timer);
}, [discountNotice]);
const editReviewedLines = useCallback((update, { preservePriceAt = null } = {}) => {
  const previous = adjustmentStateRef.current;
  const candidate = update(previous.lines);
  if (ocrCommercialSignature(candidate) === ocrCommercialSignature(previous.lines)) {
    adjustmentStateRef.current = { ...previous, lines: candidate };
    setLines(candidate);
    return;
  }
  try {
    // Restore pre-discount prices before editing: a new quantity must not retain the old discount.
    const restored = removeAppliedDiscounts(previous.lines, previous.expenses, { ...OCR_ADJUSTMENT_OPTIONS, editLines: update });
    // A price explicitly entered/chosen by the user is the final line price,
    // even when retained expenses were redistributed after a quantity edit.
    const nextLines = restored.removed && preservePriceAt !== null ? restored.lines.map((line, index) => index === preservePriceAt
      ? { ...line, unit_price: candidate[index].unit_price, price_status: candidate[index].price_status,
        price_estimate_source: candidate[index].price_estimate_source, is_free_stock: candidate[index].is_free_stock,
        free_stock_reason: candidate[index].free_stock_reason,
        total_price_in_dn: (toNumber(line.quantity) || 0) * (toNumber(candidate[index].unit_price) || 0) }
      : line) : restored.lines;
    adjustmentStateRef.current = { lines: nextLines, expenses: restored.expenses };
    setLines(nextLines);
    setExpenses(restored.expenses);
    setAdjustmentEditError("");
    if (restored.removed) setDiscountNotice((value) => value + 1);
  } catch (error) {
    setAdjustmentEditError(error.message);
  }
}, []);
const adjustmentHydrationError = initialReview.adjustmentHydrationError;

// 💾 Crear Delivery Note a partir del OCR revisado
const [suppliers, setSuppliers] = useState([]);
const [suppliersLoaded, setSuppliersLoaded] = useState(false);
const [supplierLoadError, setSupplierLoadError] = useState(false);
const [supplierLoadAttempt, setSupplierLoadAttempt] = useState(0);
const [items, setItems] = useState([]);
const [itemsLoaded, setItemsLoaded] = useState(false);
const [itemsSupplierId, setItemsSupplierId] = useState("");
const [selectedSupplier, setSelectedSupplier] = useState(initialDraft?.selectedSupplier ?? String(importData?.suggested_supplier_id || ""));
const supplierEdited = React.useRef(initialDraft?.supplierEdited ??
  Boolean(initialDraft?.selectedSupplier || initialDraft?.aiApplied));
const supplierResolutionApplied = React.useRef(false);
const [distributorResolution, setDistributorResolution] = useState(null);
const [distributorReady, setDistributorReady] = useState(false);
const manuallySelectedDistributorSupplier = React.useRef(initialDraft?.distributorSupplierChoice?.confirmed ?? false);
const previousDistributor = React.useRef(initialDraft?.distributorSupplierChoice?.distributorId ?? null);
const selectedSupplierRef = React.useRef(selectedSupplier);
selectedSupplierRef.current = selectedSupplier;
const supplierCatalogueRef = React.useRef(null);
supplierCatalogueRef.current = { suppliers, loaded: suppliersLoaded };
const pendingSupplierResolution = React.useRef(null);
const handleDistributorResolution = useCallback((resolution) => {
  setDistributorResolution(resolution);
  if (!supplierCatalogueRef.current.loaded) { pendingSupplierResolution.current = resolution; return; }
  pendingSupplierResolution.current = null;
  const currentSupplier = supplierCatalogueRef.current.suppliers.find((supplier) => supplierMatchesId(supplier, selectedSupplierRef.current));
  const preserveManualChoice = manuallySelectedDistributorSupplier.current && previousDistributor.current === (resolution.distributor?.id || null) &&
    resolution.supplier_candidates?.some((supplier) => supplierMatchesId(supplier, currentSupplier?.id ?? selectedSupplierRef.current));
  previousDistributor.current = resolution.distributor?.id || null;
  if (!resolution.distributor && !resolution.distributor_candidates?.length) {
    if (supplierEdited.current) return;
    supplierResolutionApplied.current = true;
    const nextId = String(resolution.suggested_supplier_id || "");
    if (nextId !== selectedSupplierRef.current && !supplierMatchesId(currentSupplier, nextId)) {
      setSelectedSupplier(nextId);
      // Initial OCR suggestions are revalidated once this supplier's catalogue loads.
      editReviewedLines((previous) => previous.map((line) => selectedSupplierRef.current || !nextId || hasOcrAiUnitProposal(line)
        ? resetLineForSupplier(line, nextId) : line));
    }
    return;
  }
  if (preserveManualChoice) return;
  manuallySelectedDistributorSupplier.current = false;
  const nextId = resolution.supplier_selection_required ? "" : String(resolution.suggested_supplier_id || "");
  if (nextId !== selectedSupplierRef.current && !supplierMatchesId(currentSupplier, nextId)) {
    setSelectedSupplier(nextId);
    // The first supplier validates initial OCR suggestions against its catalogue.
    editReviewedLines((previous) => previous.map((line) => selectedSupplierRef.current || !nextId || hasOcrAiUnitProposal(line)
      ? resetLineForSupplier(line, nextId) : line));
  }
}, [editReviewedLines]);
useEffect(() => {
  if (suppliersLoaded && pendingSupplierResolution.current) handleDistributorResolution(pendingSupplierResolution.current);
}, [suppliersLoaded, handleDistributorResolution]);
const [pendingPoLines, setPendingPoLines] = useState([]);
const [loadingPendingPo, setLoadingPendingPo] = useState(false);
const [supplierPickerOpen, setSupplierPickerOpen] = useState(false);
const [itemPicker, setItemPicker] = useState({ open: false, lineIndex: null });
// 🆕 Estados para editar número y fecha del albarán
const [deliveryNumber, setDeliveryNumber] = useState(initialDraft?.deliveryNumber ?? importData?.delivery_number_guess ?? "");
const [deliveryDate, setDeliveryDate] = useState(initialDraft?.deliveryDate ?? (importData?.delivery_date_guess || new Date().toISOString().split("T")[0]));
const complimentary = useComplimentaryConfirmation({ isActive: !isCreatedOcrImport(importData),
  scopeKey: JSON.stringify([businessUnitId, importData.id, selectedSupplier, deliveryDate]) });
// Keep the editor's exact values, including incomplete fields, accepted item
// matches and discount allocation snapshots. Rehydrating OCR would lose them.
const draftSnapshot = JSON.stringify({
  schema_version: 1, selectedSupplier, deliveryNumber, deliveryDate, dateConfirmed,
  supplierEdited: supplierEdited.current,
  lines, expenses, aiSupplierName, aiSupplierEvidenceId, aiApplied, aiEvidence,
  distributorSupplierChoice: { confirmed: manuallySelectedDistributorSupplier.current, distributorId: previousDistributor.current },
});
useEffect(() => {
  onDraftChange(JSON.parse(draftSnapshot));
}, [draftSnapshot, onDraftChange]);
const scannedSupplierProfile = useMemo(
  () => extractScannedSupplierProfile(parsedImport, importData),
  [importData, parsedImport]
);
const supplierCreateInitialForm = useMemo(
  () => aiSupplierName !== null ? { name: aiSupplierName } : ({
    name: scannedSupplierProfile.name || "",
    supplier_id: scannedSupplierProfile.supplier_id || "",
    tax_id: scannedSupplierProfile.tax_id || "",
    phone: scannedSupplierProfile.phone || "",
    email: scannedSupplierProfile.email || "",
    address: scannedSupplierProfile.address || "",
    contact_name: scannedSupplierProfile.contact_name || "",
  }),
  [aiSupplierName, scannedSupplierProfile]
);

const reviewedLineTotal = lines.reduce((sum, line) => {
  const quantity = toNumber(line.quantity) || 0;
  const unitPrice = line.is_free_stock ? 0 : toNumber(line.unit_price) || 0;
  return sum + quantity * unitPrice;
}, 0);
const reviewedExpenseTotal = expenses.reduce(
  (sum, expense) => sum + (expense.distribute_cost ? 0 : Math.max(0, toNumber(expense.amount) || 0)),
  0
);
const reviewedDiscountTotal = expenses.reduce((sum, expense) =>
  sum + (expense.distribute_cost ? 0 : Math.min(0, toNumber(expense.amount) || 0)), 0);
const reviewedTotal = reviewedLineTotal + reviewedExpenseTotal + reviewedDiscountTotal;

const lineProblems = lines.map((line) => {
  const selectedItemId = resolveSuggestedItemId(line);
  const selectedItem = items.find((item) => String(item.id) === String(selectedItemId));
  const itemExistsForSupplier =
    !selectedItemId ||
    !selectedSupplier ||
    !items.length ||
    items.some((item) => String(item.id) === selectedItemId);

  return {
    missingDescription: !selectedItem && !String(line.description || "").trim(),
    missingItem: !selectedItemId || !itemExistsForSupplier,
    invalidItemForSupplier: Boolean(selectedItemId && !itemExistsForSupplier),
    missingUnit: Boolean(selectedItemId && (!selectedItem || !isDeliveryNoteUnitLevelValid(selectedItem, line.unit_level))),
    missingQuantity: !isPositiveNumber(line.quantity),
    invalidPrice: String(line.unit_price ?? "").trim() !== "" && !isExplicitZeroPrice(line.unit_price) && !isPositiveNumber(line.unit_price),
  };
});

const unresolvedLineCount = lineProblems.filter(
  (problem) => problem.missingDescription || problem.missingItem || problem.missingUnit || problem.missingQuantity || problem.invalidPrice
).length;
// Empty optional rows are harmless; entered percentages must be applied first.
const adjustmentProblems = expenses.flatMap((expense) => {
  try { prepareDeliveryNoteAdjustments([expense], lines, OCR_ADJUSTMENT_OPTIONS); return []; }
  catch (error) { return [{ description: expense.description || t("Discounts"), message: error.message }]; }
});
const unresolvedExpenseCount = Math.max(Number(adjustmentHydrationError), adjustmentProblems.length);

const hasRecognizedIssuer = Boolean(distributorResolution?.distributor || distributorResolution?.distributor_candidates?.length);
const selectedSupplierObject = suppliers.find((supplier) => supplierMatchesId(supplier, selectedSupplier));
const canCreateDeliveryNote =
  !adjustmentHydrationError &&
  distributorReady &&
  (!hasRecognizedIssuer || (distributorResolution.supplier_candidates?.some((supplier) => supplierMatchesId(supplier, selectedSupplierObject?.id ?? selectedSupplier))
    && (!distributorResolution.supplier_selection_required || manuallySelectedDistributorSupplier.current))) &&
  Boolean(selectedSupplierObject) &&
  Boolean(String(deliveryNumber || "").trim()) &&
  Boolean(deliveryDate) &&
  lines.length > 0 &&
  unresolvedLineCount === 0 &&
  unresolvedExpenseCount === 0;

const pendingByItem = useMemo(() => {
  const grouped = new Map();
  pendingPoLines.forEach((line) => {
    const key = String(line.item_id);
    grouped.set(key, [...(grouped.get(key) || []), line]);
  });
  return grouped;
}, [pendingPoLines]);


const handleSaveAndCreateDN = async () => {
  if (!scopedApi.isCurrent() || !hasOcrPermission(readStoredUser(), "create")) return;
  if (closingDraft || draftBlocked) return;
  if (saveInFlight.current || aiReviewInFlight.current || complimentary.isOpen || duplicateRequest) return;
  if (!distributorReady || adjustmentHydrationError) return;
  if (hasRecognizedIssuer && (!distributorResolution.supplier_candidates?.some((supplier) => supplierMatchesId(supplier, selectedSupplierObject?.id ?? selectedSupplier))
    || (distributorResolution.supplier_selection_required && !manuallySelectedDistributorSupplier.current))) {
    alert(distributorText("Choose the supplier manually before creating the delivery note."));
    return;
  }
  console.log("[OCR→DN][START]");

  if (!selectedSupplier) {
    alert(t("Please select a supplier before saving."));
    return;
  }

  if (!selectedSupplierObject) {
    alert(purchaseSupplierError({ response: { data: { code: "SUPPLIER_INACTIVE" } } }, languageId));
    return;
  }

  if (!String(deliveryNumber || "").trim()) {
    alert(t("Please enter the delivery note number before saving."));
    return;
  }

  if (!deliveryDate) {
    alert(t("Please enter the delivery date before saving."));
    return;
  }

  if (!lines.length) {
    alert(t("There are no lines to create."));
    return;
  }

  if (unresolvedLineCount > 0) {
    alert(t("Please fix or remove all highlighted OCR lines before creating the delivery note."));
    return;
  }

  if (unresolvedExpenseCount > 0) {
    alert(t("Please complete or remove all highlighted additional expenses before creating the delivery note."));
    return;
  }

  // 🧭 Validar fecha antes de continuar (solo diferencia de días)
  const selectedDate = new Date(deliveryDate);
  const now = new Date();

  if (!Number.isFinite(selectedDate.getTime())) {
    alert(t("Please enter a valid delivery date before saving."));
    return;
  }

  const diffDays = Math.abs((selectedDate - now) / (1000 * 60 * 60 * 24));

  saveInFlight.current = true;
  setSaving(true);
  let attemptedPayload = null;
  try {
    if (diffDays > 7) {
      const confirmProceed = await dateConfirmation.confirm(
        t("The delivery note date ({date}) is more than one week apart from today. Do you still want to register it?", { date: selectedDate.toISOString().split("T")[0] }),
        { tone: "success" }
      );
      if (!confirmProceed || !mounted.current) return;
    }

    // Price decisions belong to verification; OCR only creates a draft.
    const preparedLines = lines.map((line) => normalizeComplimentaryPrice(line, "unit_price"));
    if (!mounted.current) return;
    setLines(preparedLines);

    // Construir payload compatible con el endpoint backend
    const payload = {
      ocr_import_id: importData.id,
      ocr_supplier_text: importData.supplier_guess || parsedImport?.header?.supplier || "",
      ocr_supplier_names: [...new Set([
        importData.supplier_guess, parsedImport?.header?.supplier,
        aiSupplierEvidenceId === selectedSupplier ? aiSupplierName : null,
      ].filter((name) => typeof name === "string" && name.trim()).map((name) => name.trim()))],
      supplier_id: Number(selectedSupplierObject.id),
      ...(distributorResolution?.distributor?.id ? { distributor_id: distributorResolution.distributor.id } : {}),
      supplier_name: selectedSupplierName || null,
      delivery_note_number: deliveryNumber.trim() || "OCR-MISSING",
      delivery_note_date: deliveryDate || new Date().toISOString().split("T")[0],
      expenses: prepareDeliveryNoteAdjustments(expenses, preparedLines, OCR_ADJUSTMENT_OPTIONS),
      lines: preparedLines.map((l) => {
        const itemId = resolveSuggestedItemId(l);
        const selectedItem = items.find((item) => String(item.id) === String(itemId));
        return {
          item_id: parseInt(itemId),
          item_name: selectedItem?.name || l.suggested_item?.name || null,
          ocr_description: String(l.ocr_description || l.description || "").trim(),
          description: String(l.description || "").trim(),
          quantity_in_dn: toNumber(l.quantity) || 0,
          unit_price_in_dn: toNumber(l.unit_price) || 0,
          expiration_date: expirationDatePayload(l, selectedItem),
          is_free_stock: Boolean(l.is_free_stock),
          price_status: l.price_status,
          price_estimate_source: l.price_estimate_source || null,
          free_stock_reason: l.is_free_stock ? String(l.free_stock_reason || "").trim() || null : null,
          unit_level: l.unit_level,
          po_receipts: Array.isArray(l.po_receipts) ? l.po_receipts : [],
        };
      }),
    };

    const endpoint = "/api/purchasing/delivery-notes-direct";

    if (!hasOcrPermission(readStoredUser(), "create")) return;
    attemptedPayload = payload;
    const response = await scopedApi.post(endpoint, payload);
    setLearningFailed(response.data?.ocr_learning?.status === "failed");
    onDraftComplete();
    if (!mounted.current) return;

    console.log("[OCR→DN][SUCCESS] HTTP", response.status);
    console.log("[OCR→DN][RESPONSE DATA]", response.data);

    // The note is already saved; a list refresh failure must not invite a duplicate creation.
    try {
      await onCreated?.(response.data?.delivery_note);
    } catch (refreshError) {
      console.warn("[OCR→DN][LIST REFRESH]", refreshError);
    }
    if (!mounted.current) return;

    // 🔁 Refrescar lista de OCR imports sin esperar cierre manual
    try {
      await scopedApi.get("/api/purchasing/ocr-imports?refresh=true");
      console.log("[OCR][REFRESH] Forced refresh after DN creation");
    } catch (refreshErr) {
      console.warn("⚠️ [OCR][REFRESH] Failed to refresh OCR imports:", refreshErr.message);
    }

    // Show a brief Pilcore confirmation before returning to the inbox automatically.
    if (mounted.current) setCreatedId(response.data?.delivery_note?.id || "unknown");

  } catch (error) {
    if (isOcrScopeError(error)) return;
    if (mounted.current && attemptedPayload && error.response?.data?.code === "DELIVERY_NOTE_DUPLICATE") {
      const notes = error.response.data.duplicate_delivery_notes;
      if (Array.isArray(notes) && notes.some((note) => Number.isSafeInteger(Number(note.id)) && Number(note.id) > 0)) {
        setDuplicateRequest({ payload: attemptedPayload, notes });
        return;
      }
    }
    console.error("❌ [OCR→DN][ERROR] Request failed");
    console.error("➡️ Status:", error.response?.status ?? "NO STATUS");
    console.error("➡️ URL:", error.config?.url ?? "NO URL");
    console.error("➡️ Message:", error.message ?? "NO MESSAGE");
    console.error("➡️ Response data:", error.response?.data ?? "(empty)");

    if (mounted.current) alert(purchaseSupplierError(error, languageId) || deliveryNoteDuplicateError(error, languageId) || (t("Error creating delivery note: ") + (error.response?.data?.error || error.message)));
  } finally {
    saveInFlight.current = false;
    if (mounted.current) setSaving(false);
  }
};

const handleDuplicateResolved = async (data) => {
  if (!mounted.current || !scopedApi.isCurrent()) return;
  // Mark completed before refreshing anything: never replay an applied update.
  setUpdatedNote(data.delivery_note);
  setDuplicateRequest(null);
  onDraftComplete();
  window.dispatchEvent(new CustomEvent("refreshDeliveryNotesList"));
  try { await onCreated?.(data.delivery_note); }
  catch (refreshError) { console.warn("[OCR][DUPLICATE][LIST REFRESH]", refreshError); }
  try { await scopedApi.get("/api/purchasing/ocr-imports?refresh=true"); }
  catch (refreshError) { console.warn("[OCR][DUPLICATE][INBOX REFRESH]", refreshError); }
};


// 🧠 LOG INICIAL
useEffect(() => {
  console.log("[OCR][DEBUG][MOUNT] importData:", importData);
  console.log("[OCR][DEBUG][MOUNT] suggested_supplier_id:", importData?.suggested_supplier_id);
  console.log("[OCR][DEBUG][MOUNT] suggested_supplier_name:", importData?.suggested_supplier_name);
}, [importData]);

// 🟢 Cargar proveedores al abrir popup
useEffect(() => {
  let cancelled = false;
  setSuppliersLoaded(false);
  setSupplierLoadError(false);
  const fetchSuppliers = async () => {
    try {
      console.log("[OCR][SUPPLIERS][FETCH] Fetching suppliers for BU:", businessUnitId);

      const res = await scopedApi.get(`/api/purchasing/suppliers/options?business_unit_id=${businessUnitId}`);
      if (cancelled) return;

      console.log("[OCR][SUPPLIERS][FETCH] Response count:", res.data?.length || 0);
      console.table(res.data.map(s => ({ id: s.id, name: s.name })));

      // 🧩 Orden alfabético ascendente
      const sorted = sortByName(activePurchaseSuppliers(res.data));

      setSuppliers(sorted);
      setSuppliersLoaded(true);

    } catch (err) {
      if (cancelled) return;
      console.error("❌ [OCR][SUPPLIERS][ERROR]", err);
      setSuppliers([]);
      setSupplierLoadError(true);
    }
  };

  if (!businessUnitId) return;
  fetchSuppliers();
  return () => { cancelled = true; };
}, [businessUnitId, scopedApi, supplierLoadAttempt]);

// 🧩 Sincronizar el proveedor sugerido al llegar suppliers o importData
useEffect(() => {
  if (!importData || initialDraft || aiApplied || supplierEdited.current || supplierResolutionApplied.current || distributorResolution?.distributor || distributorResolution?.distributor_candidates?.length) return;
  console.log("[OCR][SYNC][TRIGGER] suppliers:", suppliers.length, "selectedSupplier:", selectedSupplier);

  const suggestedId = importData?.suggested_supplier_id;
  const suggestedName = importData?.suggested_supplier_name;

  console.log("[OCR][SYNC][DATA] suggested_supplier_id:", suggestedId, "suggested_supplier_name:", suggestedName);

  if (suggestedId && !selectedSupplier && suppliers.some((supplier) => String(supplier.id) === String(suggestedId))) {
    console.log("[OCR][SYNC][APPLY] Applying suggested supplier:", suggestedId, suggestedName);
    setSelectedSupplier(String(suggestedId));
  } else if (suppliers.length && selectedSupplier) {
    const exists = suppliers.some((s) => String(s.id) === String(selectedSupplier));
    console.log("[OCR][SYNC][VALIDATE] exists:", exists);
    if (!exists && suggestedId && suppliers.some((supplier) => String(supplier.id) === String(suggestedId))) {
      console.warn("[OCR][SYNC][FORCE] Forcing supplier to:", suggestedId);
      setSelectedSupplier(String(suggestedId));
    }
  }
}, [aiApplied, importData, initialDraft, selectedSupplier, suppliers, distributorResolution]);

  useEffect(() => {
    let cancelled = false;
    setItems([]);
    setItemsLoaded(false);
    setItemsSupplierId("");
    if (!selectedSupplier) return;

    const fetchItems = async () => {
      try {
        const res = await scopedApi.get(`/api/purchasing/items/by-supplier/${selectedSupplier}`, { params: { ocr_item_aliases: "1" } });
        if (cancelled) return;
        console.log("[OCR][ITEMS][DEBUG]", res.data.length, "items loaded for supplier", selectedSupplier);

        // 🧩 Orden alfabético ascendente
        const sorted = sortByName(res.data || []);

        setItems(sorted);
        setItemsSupplierId(selectedSupplier);
        setItemsLoaded(true);
      } catch (err) {
        if (cancelled) return;
        console.error("❌ [OCR][ITEMS][ERROR]", err);
        setItems([]);
      }
    };
    fetchItems();
    return () => { cancelled = true; };
  }, [selectedSupplier, scopedApi]);



  useEffect(() => {
    if (!itemsLoaded || itemsSupplierId !== selectedSupplier) return;
    setLines((prev) => {
      let changed = false;
      const next = prev.map((line) => {
        const linked = completePendingOcrAiItemMatch(line, items, selectedSupplier);
        if (linked !== line) { changed = true; return linked; }
        if (line._aiNeedsItemSelection) return line;
        const selectedId = resolveSuggestedItemId(line);
        const matchedItem = findReliableOcrItemForLine(line, items);
        const selectedExists = selectedId && matchedItem && String(matchedItem.id) === selectedId;
        // Recover saved AI drafts that previously required choosing the only unit.
        const printedEvidence = readOcrUnitEvidence(line);
        const canMatchPrintedUnit = !aiApplied && !hasOcrAiUnitProposal(line) && !line._ocrUnitSelectionManual &&
          Boolean(printedEvidence.unit_label || printedEvidence.unit_label_needs_review);
        if (line._aiNeedsUnitSelection && !canMatchPrintedUnit && (!selectedExists || buildDeliveryNoteUnitOptions(matchedItem).length !== 1)) return line;
        if (selectedExists && line._ocrUnitSelectionManual && !line.unit_level) return line;
        const suggestedUnitLevel = (item) => aiApplied || hasOcrAiUnitProposal(line)
          ? defaultDeliveryNoteUnitLevel(item, { requireExplicitWhenConvertible: true })
          : matchOcrUnitLevel(item, line);
        if (selectedExists) {
          const selectedItem = items.find((item) => String(item.id) === String(selectedId));
          const unitLevel = isDeliveryNoteUnitLevelValid(selectedItem, line.unit_level)
            ? line.unit_level
            : suggestedUnitLevel(selectedItem);
          if (
            String(line.suggested_item_id || "") === String(selectedId) &&
            String(line.unit_level || "") === String(unitLevel || "") && !line._aiNeedsUnitSelection
          ) return line;
          changed = true;
          return { ...updateComplimentaryLine(
            updateComplimentaryLine(line, "suggested_item_id", String(selectedId), OCR_COMPLIMENTARY_FIELDS),
            "unit_level", unitLevel, OCR_COMPLIMENTARY_FIELDS
          ), _aiNeedsUnitSelection: !unitLevel, po_receipts: line.unit_level === unitLevel ? line.po_receipts : [] };
        }

        if (!matchedItem) {
          if (!selectedId && !line.suggested_item) return line;
          changed = true;
          return { ...line, suggested_item_id: "", suggested_item: null, unit_level: "",
            po_receipts: [], is_free_stock: false, free_stock_reason: "", _itemSelectionConfirmed: false };
        }

        changed = true;
        const unitLevel = suggestedUnitLevel(matchedItem);
        return {
          ...updateComplimentaryLine(line, "suggested_item_id", String(matchedItem.id), OCR_COMPLIMENTARY_FIELDS),
          suggested_item_id: String(matchedItem.id),
          unit_level: unitLevel,
          _aiNeedsUnitSelection: !unitLevel,
          _ocrUnitSelectionManual: false,
          po_receipts: [], is_free_stock: false, free_stock_reason: "", _itemSelectionConfirmed: false,
          suggested_item: {
            id: matchedItem.id,
            name: matchedItem.name,
            code: matchedItem.code,
          },
        };
      });
      return changed ? next : prev;
    });
  }, [items, itemsLoaded, itemsSupplierId, selectedSupplier, aiApplied]);

  // Verify suggested ids exist in the options (helps catching mismatches)
  useEffect(() => {
    if (!items?.length || !lines?.length) return;
    lines.forEach((line, idx) => {
      const present = items.some(it => String(it.id) === String(line.suggested_item_id));
      console.log(`[OCR][ITEMS][CHECK] line#${idx+1} sug=${line.suggested_item_id} present=${present}`);
    });
  }, [items, lines]);

  useEffect(() => {
    if (!selectedSupplier) {
      setPendingPoLines([]);
      return;
    }

    const itemIds = [...new Set(lines.map((line) => resolveSuggestedItemId(line)).filter(Boolean).map(String))];
    if (!itemIds.length) {
      setPendingPoLines([]);
      return;
    }

    let cancelled = false;
    const loadPendingPoLines = async () => {
      setLoadingPendingPo(true);
      try {
        const response = await scopedApi.get("/api/purchasing/delivery-notes-direct/pending-po-lines", {
          params: { supplier_id: selectedSupplier, item_ids: itemIds.join(",") },
        });
        if (!cancelled) setPendingPoLines(Array.isArray(response.data) ? response.data : []);
      } catch (requestError) {
        console.error("❌ [OCR][PENDING_PO][ERROR]", requestError);
        if (!cancelled) setPendingPoLines([]);
      } finally {
        if (!cancelled) setLoadingPendingPo(false);
      }
    };

    loadPendingPoLines();
    return () => {
      cancelled = true;
    };
  }, [lines, selectedSupplier, scopedApi]);

  // An unmatched AI name becomes evidence only when the user links it. A later
  // correction must not teach that already-linked name to a different supplier.
  const confirmSupplierEvidence = (supplierId) => {
    supplierEdited.current = true;
    if (!aiSupplierName) return;
    if (aiSupplierEvidenceId && aiSupplierEvidenceId !== supplierId) {
      setAiSupplierName(null);
      setAiSupplierEvidenceId("");
    } else setAiSupplierEvidenceId(supplierId);
  };

  // 🧠 Cambiar proveedor manualmente
  const handleSelectSupplier = (supplier) => {
    const newId = supplier?.id ? String(supplier.id) : "";
    if (hasRecognizedIssuer && !distributorResolution.supplier_candidates?.some((candidate) => supplierMatchesId(candidate, newId))) return;
    // Keep the draft's evidence context and physical PO links for the same group.
    // The new delivery note uses the active principal when it is submitted.
    if (supplierMatchesId(supplier, selectedSupplier)) {
      if (newId === selectedSupplier) confirmSupplierEvidence(newId);
      else supplierEdited.current = true;
      manuallySelectedDistributorSupplier.current = hasRecognizedIssuer;
      setSupplierPickerOpen(false);
      return;
    }
    confirmSupplierEvidence(newId);
    manuallySelectedDistributorSupplier.current = hasRecognizedIssuer;
    setSelectedSupplier(newId);
    setSupplierPickerOpen(false);
    if (newId === selectedSupplier) return;
    editReviewedLines((prev) => prev.map((line) => resetLineForSupplier(line, newId)));
  };

  const handleSupplierCreatedFromOcr = (createdSupplier) => {
    confirmSupplierEvidence(String(createdSupplier.id));
    setSuppliers((prev) => sortByName([...prev.filter((supplier) => String(supplier.id) !== String(createdSupplier.id)), createdSupplier]));
    setSelectedSupplier(String(createdSupplier.id));
    editReviewedLines((prev) => prev.map((line) => resetLineForSupplier(line, String(createdSupplier.id))));
    return createdSupplier;
  };

  const handleSelectItem = (lineIndex, item) => {
    if (lineIndex == null || !item?.id) return;
    editReviewedLines((prev) =>
      prev.map((line, currentIndex) => {
        if (currentIndex !== lineIndex) return line;
        const sameItem = String(line.suggested_item_id || "") === String(item.id);
        const unitLevel = sameItem && isDeliveryNoteUnitLevelValid(item, line.unit_level)
          ? line.unit_level
          : aiApplied || hasOcrAiUnitProposal(line)
            ? defaultDeliveryNoteUnitLevel(item, { requireExplicitWhenConvertible: true })
            : matchOcrUnitLevel(item, line);
        const selectedLine = updateComplimentaryLine(line, "suggested_item_id", String(item.id), OCR_COMPLIMENTARY_FIELDS);
        return {
              ...updateComplimentaryLine(selectedLine, "unit_level", unitLevel, OCR_COMPLIMENTARY_FIELDS),
              suggested_item: {
                ...(line.suggested_item || {}),
                id: item.id,
                name: item.name,
                code: item.code,
              },
              po_receipts: sameItem && line.unit_level === unitLevel ? line.po_receipts : [],
              _ocrUnitSelectionManual: sameItem ? line._ocrUnitSelectionManual : false,
              _aiNeedsItemSelection: false,
              _aiNeedsUnitSelection: !unitLevel,
              _aiPendingItemMatch: false,
              _itemSelectionConfirmed: true,
            };
      }
      )
    );
    setItemPicker({ open: false, lineIndex: null });
  };

  const handleItemCreatedFromOcr = (lineIndex, createdItem) => {
    setItems((prev) => sortByName([...prev.filter((item) => String(item.id) !== String(createdItem.id)), createdItem]));
    handleSelectItem(lineIndex, createdItem);
  };


  // 🧮 Modificar valores de líneas
  const handleValueChange = (index, field, value) => {
    editReviewedLines((prev) =>
      prev.map((line, currentIndex) =>
        currentIndex === index
          ? ["quantity", "unit_price"].includes(field) && toNumber(line[field]) !== null && toNumber(line[field]) === toNumber(value)
            ? { ...line, [field]: value }
            : {
              ...updateComplimentaryLine(line, field, value, OCR_COMPLIMENTARY_FIELDS),
              ...(["description", "supplier_code", "suggested_item_id", "unit_level"].includes(field) ? { _aiPendingItemMatch: false, _aiMatchSupplierId: "" } : {}),
              ...(field === "unit_level" ? { _aiNeedsUnitSelection: !value, _ocrUnitSelectionManual: true } : {}),
              ...(field === "suggested_item_id" && String(line.suggested_item_id || "") !== String(value || "")
                ? { po_receipts: [] }
                : {}),
              ...(field === "unit_level" && line.unit_level !== value ? { po_receipts: [] } : {}),
            }
          : line
      ), { preservePriceAt: field === "unit_price" ? index : null }
    );
  };

  const editLinePrice = async (index) => {
    if (saving || closingDraft || aiBusy || draftBlocked || !scopedApi.isCurrent()) return;
    const source = lines[index];
    const getItem = (line) => items.find((item) => String(item.id) === resolveSuggestedItemId(line));
    const prepared = await complimentary.editLine(source, {
      priceKey: "unit_price", quantityKey: "quantity",
      getItemLabel: (line) => selectedItemLabel(getItem(line), line.description || t("Item")),
      getUnitLabel: (line) => buildDeliveryNoteUnitOptions(getItem(line), undefined, languageId)
        .find((option) => option.level === line.unit_level)?.label || line.unit_level,
      getLastPrice: (line) => fetchDeliveryNoteReferencePrice({ line: { ...line, item_id: resolveSuggestedItemId(line) },
        item: getItem(line), businessUnitId, supplierId: selectedSupplier, date: deliveryDate,
        requestClient: scopedApi, languageId }),
    });
    if (!prepared || !mounted.current || !scopedApi.isCurrent() || adjustmentStateRef.current.lines[index] !== source) return;
    const statusOnly = toNumber(prepared.unit_price) === toNumber(source.unit_price)
      && prepared.price_status !== "missing" && !prepared.is_free_stock
      && (!prepared.price_estimate_source || prepared.price_estimate_source === "manual");
    editReviewedLines((previous) => previous.map((line, lineIndex) => lineIndex === index ? { ...line,
      unit_price: statusOnly ? line.unit_price : prepared.unit_price,
      price_status: prepared.price_status,
      price_estimate_source: prepared.price_estimate_source, is_free_stock: prepared.is_free_stock,
      free_stock_reason: prepared.free_stock_reason,
    } : line), { preservePriceAt: statusOnly ? null : index });
  };

  const removeReviewedLine = (index) => {
    editReviewedLines((prev) => prev.filter((_, currentIndex) => currentIndex !== index));
  };

  const recalcTotal = (qty, price) => {
    const q = parseFloat(qty) || 0;
    const p = parseFloat(price) || 0;
    return (q * p).toFixed(2);
  };

  const reviewFlags = filterResolvedOcrReviewFlags(originalReviewFlags, {
    supplierMatched: Boolean(selectedSupplierObject) || Boolean(distributorResolution?.distributor), deliveryNumber, deliveryDate, dateConfirmed,
    lines, expenses, aiTotals: aiEvidence.totals,
  });
  const reviewErrors = reviewFlags.filter((flag) => flag.requires_review || flag.severity === "error");
  const reviewWarnings = reviewFlags.filter((flag) => !flag.requires_review && flag.severity !== "error");
  const supplierNeedsReview =
    !selectedSupplier ||
    hasQualityFlag(reviewFlags, "missing_supplier") ||
    hasQualityFlag(reviewFlags, "supplier_not_matched");
  const numberNeedsReview =
    !String(deliveryNumber || "").trim() ||
    hasQualityFlag(reviewFlags, "missing_document_number");
  const dateNeedsReview = !deliveryDate || hasQualityFlag(reviewFlags, "missing_date");
  const reviewInputClass = (needsReview) =>
    `form-control ${needsReview ? "border-warning" : ""}`;
  const selectedSupplierName =
    selectedSupplierObject?.name ||
    (initialDraft || aiApplied || supplierResolutionApplied.current || distributorResolution?.distributor || distributorResolution?.distributor_candidates?.length ? "" : importData?.suggested_supplier_name) ||
    "";
  const canUseAiReview = Number(currentUser?.id ?? currentUser?.user_id ?? currentUser?.sub) === 1 ||
    currentUser?.permissions?.access_settings === true || currentUser?.permissions?.["purchasing.delivery_notes.create"] === true;
  const aiReviewDraft = { supplierId: selectedSupplier, supplierName: selectedSupplierName || aiSupplierName || "",
    supplierOcrName: aiSupplierName,
    supplierLocked: Boolean(distributorResolution?.distributor || distributorResolution?.distributor_candidates?.length),
    deliveryNumber, deliveryDate, lines, expenses };
  const applyAiReview = (next) => {
    if (expenses.some((expense) => expense.distribute_cost)) {
      alert(t("Undo expense and discount distributions before applying another AI review."));
      return;
    }
    const distributorControlsSupplier = distributorResolution?.distributor || distributorResolution?.distributor_candidates?.length;
    supplierEdited.current = true;
    setSelectedSupplier(distributorControlsSupplier ? selectedSupplier : next.supplierId);
    setAiSupplierName(distributorControlsSupplier ? null : next.supplierOcrName);
    setAiSupplierEvidenceId(distributorControlsSupplier ? "" : next.supplierId);
    setAiEvidence((previous) => ({ totals: next.aiTotals, warnings: next.aiWarnings, summary: next.aiSummary,
      applicationKey: (previous.applicationKey || 0) + 1 }));
    if (next.aiDateConfirmed) setDateConfirmed(true);
    setDeliveryNumber(next.deliveryNumber);
    setDeliveryDate(next.deliveryDate);
    setLines(next.lines);
    setExpenses(next.expenses);
    setAiApplied(true);
  };

  if (createdId !== null) return <OcrCreationSuccess createdId={createdId} learningFailed={learningFailed} onClose={onClose} />;
  if (updatedNote) return <OcrDuplicateSuccess note={updatedNote} onClose={onClose} onViewDeliveryNote={onViewDeliveryNote} />;

  return (
    <div className="ocrp-reviewOverlay pilcore-purchasing-overlay purchasing-receiving-ui" data-theme={themeId}>
      <OriginalDocumentLayout original={originalViewer.original} onClose={originalViewer.close} dialogs={<>
        <OcrSupplierPickerModal
          open={supplierPickerOpen}
          suppliers={hasRecognizedIssuer ? suppliers.filter((supplier) => distributorResolution.supplier_candidates?.some((candidate) => String(candidate.id) === String(supplier.id))) : suppliers}
          selectedSupplierId={selectedSupplier}
          detectedSupplier={hasRecognizedIssuer ? selectedSupplierName : aiSupplierName ?? (importData.supplier_guess || parsedImport?.header?.supplier || importData.suggested_supplier_name || "")}
          distributorName={distributorResolution?.distributor?.name}
          initialForm={supplierCreateInitialForm}
          onSelect={handleSelectSupplier}
          onCreated={handleSupplierCreatedFromOcr}
          businessUnitId={businessUnitId}
          scopedApi={scopedApi}
          allowCreate={!hasRecognizedIssuer}
          onClose={() => setSupplierPickerOpen(false)}
        />
        {itemPicker.open && <OcrItemPickerModal
          key={itemPicker.lineIndex}
          scopedApi={scopedApi}
          lineIndex={itemPicker.lineIndex}
          line={lines[itemPicker.lineIndex]}
          items={items}
          selectedItemId={lines[itemPicker.lineIndex]?.suggested_item_id}
          selectedSupplierName={selectedSupplierName}
          selectedSupplierId={selectedSupplier}
          onSelect={handleSelectItem}
          onAdded={handleItemCreatedFromOcr}
          onClose={() => setItemPicker({ open: false, lineIndex: null })}
        />}
      </>}>
      <div className="ocrp-reviewModal" role="dialog" aria-modal={!originalViewer.original && !complimentary.isOpen && !duplicateRequest} aria-hidden={complimentary.isOpen || Boolean(duplicateRequest) || undefined} aria-label={t("Review import {id}", { id: importData.id })}>
        <header className="ocrp-reviewHead">
          <div>
            <h2 className="ocrp-title">{t("Review Import #")}{importData.id}</h2>
            <div className="ocrp-sub">
              {importData.original_file_name || t("Document")} · {lines.length} {t("lines")} </div>
          </div>
          <div className="ocrp-headActions">
            {importData?.s3_key && (
              <button
                type="button"
                className="ocrp-btn ocrp-btn--info"
                onClick={(event) => onViewOriginal?.(importData, event)}
                title={t("View original delivery note")}
              >
                <FaEye />
                <span>{t("Original")}</span>
              </button>
            )}
            <span className={statusClassName(importData.status)}>
              {t(statusMeta(importData.status).label)}
            </span>
            <button type="button" className="ocrp-btn ocrp-btn--close" onClick={onClose} disabled={saving || closingDraft} title={t("Close")}>
              <FaTimes />
            </button>
          </div>
        </header>

        <main className="ocrp-reviewBody" aria-busy={saving || aiBusy}>
          {!suppliersLoaded && <div className="ocrp-alert ocrp-alert--info" role={supplierLoadError ? "alert" : "status"}>
            {t(supplierLoadError ? "Could not load suppliers for this business unit." : "Loading suppliers...")}
            {supplierLoadError && <button type="button" className="ocrp-btn" onClick={() => setSupplierLoadAttempt((attempt) => attempt + 1)}>{t("Retry")}</button>}
          </div>}
          <OcrAiReviewPanel importData={importData} businessUnitId={businessUnitId} requestClient={scopedApi} review={aiReviewDraft}
            items={items} itemsLoaded={itemsLoaded} itemsSupplierId={itemsSupplierId} suppliers={suppliers} disabled={saving || closingDraft || draftBlocked || !distributorReady || !suppliersLoaded || !canUseAiReview || supplierPickerOpen || itemPicker.open}
            onApply={applyAiReview} onBusyChange={handleAiBusy} />
          {aiApplied && <OcrAiReviewComments key={aiEvidence.applicationKey} summary={aiEvidence.summary} comments={aiEvidence.warnings}
            notice={t("Proposal applied to this draft. Review the fields and item links before creating the delivery note.")} />}
          {aiApplied && !selectedSupplierObject && aiSupplierName && <p className="ocrp-alert ocrp-alert--warning" role="alert">
            {t("AI supplier: {supplier}. Select or create its local supplier record to finish linking it.", { supplier: aiSupplierName })}
          </p>}
          <fieldset disabled={saving || aiBusy || closingDraft || draftBlocked || Boolean(duplicateRequest)} style={{ display: "contents", border: 0, padding: 0, margin: 0 }}>
          {!isCreatedOcrImport(importData) && <OcrDistributorPanel importId={importData.id} scopedApi={scopedApi} disabled={saving || aiBusy}
            onResolution={handleDistributorResolution} onReadyChange={setDistributorReady} />}
          <section className="ocrp-metrics" aria-label={t("Review summary")}>
            <div className="ocrp-metric">
              <span>{t("Lines")}</span>
              <strong>{lines.length}</strong>
            </div>
            <div className="ocrp-metric">
              <span>{t("Expenses")}</span>
              <strong>{expenses.length}</strong>
            </div>
            <div className="ocrp-metric">
              <span>{t("Issues")}</span>
              <strong>{reviewErrors.length + unresolvedLineCount + unresolvedExpenseCount}</strong>
            </div>
            <div className="ocrp-metric">
              <span>{t("Total")}</span>
              <strong>{reviewedTotal.toFixed(2)} €</strong>
            </div>
          </section>

          {(reviewFlags.length > 0 || unresolvedLineCount > 0 || unresolvedExpenseCount > 0) && (
            <section className="ocrp-panel">
              <div className="ocrp-panelHead">
                <h3 className="ocrp-panelTitle">{t("Review checks")}</h3>
              </div>
              <div className="ocrp-checkList">
                {reviewErrors.map((flag) => (
                  <div key={flag.code} className="ocrp-check ocrp-check--warning">
                    <span className="ocrp-checkBadge ocrp-checkBadge--warning">{t("Fix")}</span>
                    <span>{t(flag.message || flag.code)}</span>
                  </div>
                ))}
                {reviewWarnings.map((flag) => (
                  <div key={flag.code} className="ocrp-check ocrp-check--info">
                    <span className="ocrp-checkBadge ocrp-checkBadge--info">{t("Info")}</span>
                    <span>{t(flag.message || flag.code)}</span>
                  </div>
                ))}
                {unresolvedLineCount > 0 && (
                  <div className="ocrp-check ocrp-check--warning">
                    <span className="ocrp-checkBadge ocrp-checkBadge--warning">{t("Fix")}</span>
                    <span>
                      {unresolvedLineCount} {t("line")}{unresolvedLineCount === 1 ? "" : t("s")} {t("need item, description or quantity.")} </span>
                  </div>
                )}
                {adjustmentProblems.map((problem, index) => (
                  <div className="ocrp-check ocrp-check--warning" key={`adjustment-${index}`}>
                    <span className="ocrp-checkBadge ocrp-checkBadge--warning">{t("Fix")}</span>
                    <span>{problem.description}: {ocrAdjustmentMessage(problem.message, languageId)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="ocrp-reviewGrid">
            <div className="ocrp-panel">
              <div className="ocrp-panelHead">
                <h3 className="ocrp-panelTitle">{t("OCR detected")}</h3>
              </div>
              <div className="ocrp-metrics" style={{ gridTemplateColumns: "repeat(2,minmax(0,1fr))" }}>
                <div className="ocrp-metric">
                  <span>{t("Supplier")}</span>
                  <strong title={hasRecognizedIssuer ? selectedSupplierName : importData.supplier_guess || ""}>{(hasRecognizedIssuer ? selectedSupplierName : importData.supplier_guess) || "—"}</strong>
                </div>
                <div className="ocrp-metric">
                  <span>{t("Matched")}</span>
                  <strong title={selectedSupplierName || ""}>{selectedSupplierName || "—"}{selectedSupplierName && distributorResolution?.distributor?.name && <em className="ocrp-distributorName">{distributorResolution.distributor.name}</em>}</strong>
                </div>
                <div className="ocrp-metric">
                  <span>{t("Number")}</span>
                  <strong>{importData.delivery_number_guess || "—"}</strong>
                </div>
                <div className="ocrp-metric">
                  <span>{t("Date")}</span>
                  <strong>{importData.delivery_date_guess || "—"}</strong>
                </div>
                <div className="ocrp-metric">
                  <span>{t("OCR Total")}</span>
                  <strong>{formatCurrency(qualityMetrics.detected_total ?? importData.total_guess)}</strong>
                </div>
                <div className="ocrp-metric">
                  <span>{t("Line Sum")}</span>
                  <strong>
                    {qualityMetrics.line_total_sum != null ? formatCurrency(qualityMetrics.line_total_sum) : "—"}
                  </strong>
                </div>
              </div>
            </div>

            <div className="ocrp-panel">
              <div className="ocrp-panelHead">
                <h3 className="ocrp-panelTitle">{t("Delivery note header")}</h3>
              </div>
              <div className="ocrp-fieldGrid">
                <div className="ocrp-field">
                  <label>{t("Supplier")}</label>
                  <button
                    type="button"
                    className={`ocrp-pickerButton ${supplierNeedsReview ? "border-warning" : ""}`}
                    onClick={() => setSupplierPickerOpen(true)}
                  >
                    <span className="ocrp-pickerText">
                      <span className="ocrp-pickerMain">{selectedSupplierName || aiSupplierName || t(hasRecognizedIssuer ? "Choose supplier" : "Choose or create supplier")}{selectedSupplierName && distributorResolution?.distributor?.name && <em className="ocrp-distributorName">{distributorResolution.distributor.name}</em>}</span>
                      <span className="ocrp-pickerMeta">
                        {selectedSupplierObject
                          ? [selectedSupplierObject.supplier_id, selectedSupplierObject.tax_id].filter(Boolean).join(" · ") ||
                            t("Selected supplier")
                          : t(aiSupplierName ? "AI proposal · select or create supplier" : "Search or create from OCR")}
                      </span>
                    </span>
                    <span className="ocrp-pickerChevron">{t("Browse")}</span>
                  </button>
                </div>
                <div className="ocrp-field">
                  <label>{t("Delivery Note Number")}</label>
                  <input
                    type="text"
                    className={reviewInputClass(numberNeedsReview)}
                    value={deliveryNumber}
                    onChange={(event) => setDeliveryNumber(event.target.value)}
                    placeholder={t("Number")}
                  />
                </div>
                <div className="ocrp-field">
                  <label>{t("Delivery Date")}</label>
                  <input
                    type="date"
                    className={reviewInputClass(dateNeedsReview)}
                    value={deliveryDate}
                    onChange={(event) => { setDeliveryDate(event.target.value); setDateConfirmed(true); }}
                  />
                </div>
              </div>
              <div className="ocrp-liveTotal">
                <div className="ocrp-liveTotalBreakdown">
                  <span>{t("Lines")}: <strong>{formatCurrency(reviewedLineTotal)}</strong></span>
                  <span>{t("Expenses")}: <strong>{formatCurrency(reviewedExpenseTotal)}</strong></span>
                  {reviewedDiscountTotal !== 0 && <span>{t("Discounts")}: <strong>{formatCurrency(reviewedDiscountTotal)}</strong></span>}
                </div>
                <div className="ocrp-liveTotalAmount">
                  <span>{t("Updated total")}</span>
                  <output aria-label={t("Updated total")} aria-live="polite" aria-atomic="true">
                    {formatCurrency(reviewedTotal)}
                  </output>
                </div>
              </div>
            </div>
          </section>

          <section className="ocrp-panel">
            <div className="ocrp-panelHead">
              <div>
                <h3 className="ocrp-panelTitle">{t("Lines")}</h3>
                <div className="ocrp-muted ocrp-small">
                  {unresolvedLineCount ? t("{count} need review", { count: unresolvedLineCount }) : t("All required line fields are complete")}
                </div>
              </div>
              <button
                type="button"
                className="ocrp-btn ocrp-btn--info"
                onClick={() =>
                  editReviewedLines((prev) => [
                    ...prev,
                    {
                      description: "",
                      ocr_description: "",
                      supplier_code: "",
                      quantity: "",
                      unit_price: "",
                      is_free_stock: false,
                      free_stock_reason: "",
                      unit_level: "",
                      suggested_item_id: "",
                      suggested_item: null,
                      po_receipts: [],
                    },
                  ])
                }
              >
                <FaPlus />
                <span>{t("Add line")}</span>
              </button>
            </div>

            {lines.length === 0 ? (
              <div className="ocrp-empty">
                <strong>{t("No lines detected")}</strong>
              </div>
            ) : (
              <>
                <div className="ocrp-lineShell">
                  <table className="ocrp-linesTable">
                    <thead>
                      <tr>
                        <th style={{ width: "19%" }}>{t("Description")}</th>
                        <th style={{ width: "20%" }}>{t("Item")}</th>
                        <th style={{ width: "12%", textAlign: "center" }}>{t("Unit on Note")}</th>
                        <th style={{ width: "6%", textAlign: "center" }} title={t("Qty")}>Q</th>
                        <th style={{ width: "9%", textAlign: "center" }}>{t("Unit Price")}</th>
                        <th style={{ width: "11%" }}>{t("Expiration date")}</th>
                        <th style={{ width: "9%", textAlign: "center" }}>{t("Pending PO")}</th>
                        <th style={{ width: "7%", textAlign: "right" }}>{t("Total")}</th>
                        <th style={{ width: "7%", textAlign: "right" }}>{t("Action")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, index) => {
                        const problem = lineProblems[index] || {};
                        const rowNeedsReview =
                          problem.missingDescription || problem.missingItem || problem.missingUnit || problem.missingQuantity || problem.invalidPrice;
                        const pendingCandidates = pendingByItem.get(String(line.suggested_item_id)) || [];
                        const pendingTotal = pendingCandidates.reduce((sum, candidate) => sum + pendingQtyFromPo(candidate), 0);
                        const assignedTotal = assignmentQty(line);
                        const selectedItem = items.find((item) => String(item.id) === String(line.suggested_item_id));
                        const unitOptions = buildDeliveryNoteUnitOptions(selectedItem, undefined, languageId);
                        const inventoryValues = deliveryNoteInventoryValues(line, selectedItem, undefined, languageId);

                        return (
                          <tr key={`ocr-review-line-${index}`} className={rowNeedsReview ? "ocrp-line--warning" : ""}>
                            <td>
                              <input
                                type="text"
                                className={reviewInputClass(problem.missingDescription)}
                                value={line.description || ""}
                                onChange={(event) => handleValueChange(index, "description", event.target.value)}
                                placeholder={t("Description")}
                                title={line.description}
                              />
                              <div className="ocrp-lineBadges">
                                {problem.missingDescription && (
                                  <span className="ocrp-miniBadge ocrp-miniBadge--warning">{t("Missing description")}</span>
                                )}
                                {line.suggested_item?.name && !line.suggested_item_id && (
                                  <span className="ocrp-miniBadge ocrp-miniBadge--info"> {t("Suggested:")} {line.suggested_item.name}
                                  </span>
                                )}
                                {line.ocr_description && line.ocr_description !== line.description && (
                                  <span className="ocrp-miniBadge ocrp-miniBadge--muted" title={line.ocr_description}> {t("OCR:")} {line.ocr_description}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <button
                                type="button"
                                className={`ocrp-pickerButton ${problem.missingItem ? "border-warning" : ""}`}
                                onClick={() => setItemPicker({ open: true, lineIndex: index })}
                                disabled={!selectedSupplier}
                              >
                                <span className="ocrp-pickerText">
                                  <span className="ocrp-pickerMain">
                                    {selectedItemLabel(selectedItem, line.suggested_item?.name || t("Choose or create item"))}
                                  </span>
                                  <span className="ocrp-pickerMeta">
                                    {selectedItem
                                      ? [selectedItem.supplier_code, selectedItem.category?.name, getUnitName(selectedItem.unit, languageId)].filter(Boolean).join(" · ") ||
                                        t("Selected item")
                                      : selectedSupplier
                                        ? t("Search or create from OCR")
                                        : t("Select supplier first")}
                                  </span>
                                </span>
                                <span className="ocrp-pickerChevron">{t("Browse")}</span>
                              </button>
                              {problem.missingItem && (
                                <div className="ocrp-lineNote">
                                  {problem.invalidItemForSupplier
                                    ? t("Selected item does not belong to this supplier.")
                                    : t("Select an item or remove this line.")}
                                </div>
                              )}
                            </td>
                            <td>
                              <select
                                className={`form-select ${problem.missingUnit ? "border-warning" : ""}`}
                                value={line.unit_level || ""}
                                onChange={(event) => handleValueChange(index, "unit_level", event.target.value)}
                                disabled={!selectedItem}
                              >
                                <option value="">
                                  {unitOptions.length ? t("Select unit") : t("Unit not configured")}
                                </option>
                                {unitOptions.map((option) => (
                                  <option key={`ocr-uom-${index}-${option.level}`} value={option.level}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              {selectedItem ? <span className="ocrp-unitRelation">{unitHierarchyLabel(selectedItem, undefined, languageId)}</span> : null}
                              {selectedItem && line.unit_level ? <span className="ocrp-unitPreview">{deliveryNoteInventoryPreview(line, selectedItem, undefined, languageId)}</span> : null}
                              {problem.missingUnit ? <div className="ocrp-lineNote">{t("Select the unit printed on the note.")}</div> : null}
                            </td>
                            <td>
                              <input
                                type="number"
                                className={`${reviewInputClass(problem.missingQuantity)} ocrp-num`}
                                value={line.quantity || ""}
                                step="0.00000001"
                                min="0"
                                onChange={(event) => handleValueChange(index, "quantity", event.target.value)}
                              />
                              {problem.missingQuantity && <div className="ocrp-lineNote">{t("Required")}</div>}
                            </td>
                            <td>
                              <ComplimentaryPriceField
                                className={`${reviewInputClass(problem.invalidPrice)} ocrp-num`}
                                value={line.unit_price ?? ""}
                                aria-label={`${t("Unit Price")} · ${line.description || index + 1}`}
                                isComplimentary={Boolean(line.is_free_stock)}
                                priceStatus={line.price_status}
                                priceEstimateSource={line.price_estimate_source}
                                onPriceOptions={() => editLinePrice(index)}
                                step="0.00000001"
                                min="0"
                                onChange={(event) => handleValueChange(index, "unit_price", event.target.value)}
                                disabled={saving}
                              />
                              {problem.invalidPrice ? <div className="ocrp-lineNote">{t("Enter a price of zero or greater.")}</div> : null}
                            </td>
                            <td><ExpirationDateField line={line} item={selectedItem} pendingItem={!selectedItem}
                              disabled={saving || closingDraft || draftBlocked} onChange={(value) => handleValueChange(index, "expiration_date", value)} /></td>
                            <td>
                              <div className="ocrp-poStatus">
                                <span
                                  className={`ocrp-poText ${
                                    assignedTotal > 0 ? "ocrp-poText--linked" : pendingTotal > 0 ? "ocrp-poText--pending" : ""
                                  }`}
                                >
                                  {assignedTotal > 0
                                    ? `Received ${formatQty(assignedTotal)} ${inventoryValues.option?.inventoryLabel || ""}`
                                    : pendingTotal > 0
                                      ? `${formatQty(pendingTotal)} pending`
                                      : loadingPendingPo && line.suggested_item_id
                                        ? t("Checking...")
                                        : t("No pending")}
                                </span>
                              </div>
                            </td>
                            <td className="ocrp-total">{recalcTotal(line.quantity, line.unit_price)} €</td>
                            <td style={{ textAlign: "right" }}>
                              <button
                                type="button"
                                className="ocrp-btn ocrp-btn--danger"
                                onClick={() => removeReviewedLine(index)}
                              >
                                <FaTrash />
                                <span>{t("Remove")}</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="ocrp-mobileLines">
                  {lines.map((line, index) => {
                    const problem = lineProblems[index] || {};
                    const rowNeedsReview =
                      problem.missingDescription || problem.missingItem || problem.missingUnit || problem.missingQuantity || problem.invalidPrice;
                    const pendingCandidates = pendingByItem.get(String(line.suggested_item_id)) || [];
                    const pendingTotal = pendingCandidates.reduce((sum, candidate) => sum + pendingQtyFromPo(candidate), 0);
                    const assignedTotal = assignmentQty(line);
                    const selectedItem = items.find((item) => String(item.id) === String(line.suggested_item_id));
                    const unitOptions = buildDeliveryNoteUnitOptions(selectedItem, undefined, languageId);
                    const inventoryValues = deliveryNoteInventoryValues(line, selectedItem, undefined, languageId);

                    return (
                      <article key={`ocr-mobile-line-${index}`} className="ocrp-mobileCard">
                        <div className="ocrp-mobileTop">
                          <div>
                            <div className="ocrp-mobileTitle">{t("Line")} {index + 1}</div>
                            {rowNeedsReview && <div className="ocrp-muted ocrp-small">{t("Needs review")}</div>}
                          </div>
                          <button
                            type="button"
                            className="ocrp-btn ocrp-btn--danger"
                            onClick={() => removeReviewedLine(index)}
                          >
                            <FaTrash />
                            <span>{t("Remove")}</span>
                          </button>
                        </div>
                        <div className="ocrp-field mt-2">
                          <label>{t("Description")}</label>
                          <input
                            type="text"
                            className={reviewInputClass(problem.missingDescription)}
                            value={line.description || ""}
                            onChange={(event) => handleValueChange(index, "description", event.target.value)}
                            placeholder={t("Description")}
                          />
                        </div>
                        <div className="ocrp-field mt-2">
                          <label>{t("Item")}</label>
                          <button
                            type="button"
                            className={`ocrp-pickerButton ${problem.missingItem ? "border-warning" : ""}`}
                            onClick={() => setItemPicker({ open: true, lineIndex: index })}
                            disabled={!selectedSupplier}
                          >
                            <span className="ocrp-pickerText">
                              <span className="ocrp-pickerMain">
                                {selectedItemLabel(selectedItem, line.suggested_item?.name || t("Choose or create item"))}
                              </span>
                              <span className="ocrp-pickerMeta">
                                {selectedSupplier ? t("Search or create from OCR") : t("Select supplier first")}
                              </span>
                            </span>
                            <span className="ocrp-pickerChevron">{t("Browse")}</span>
                          </button>
                        </div>
                        <div className="ocrp-field mt-2">
                          <label>{t("Unit on Note")}</label>
                          <select
                            className={`form-select ${problem.missingUnit ? "border-warning" : ""}`}
                            value={line.unit_level || ""}
                            onChange={(event) => handleValueChange(index, "unit_level", event.target.value)}
                            disabled={!selectedItem}
                          >
                            <option value="">
                              {unitOptions.length ? t("Select unit") : t("Unit not configured")}
                            </option>
                            {unitOptions.map((option) => (
                              <option key={`ocr-mobile-uom-${index}-${option.level}`} value={option.level}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {selectedItem ? <span className="ocrp-unitRelation">{unitHierarchyLabel(selectedItem, undefined, languageId)}</span> : null}
                          {selectedItem && line.unit_level ? <span className="ocrp-unitPreview">{deliveryNoteInventoryPreview(line, selectedItem, undefined, languageId)}</span> : null}
                          {problem.missingUnit ? <div className="ocrp-lineNote">{t("Select the unit printed on the note.")}</div> : null}
                        </div>
                        <div className="ocrp-mobileGrid">
                          <div className="ocrp-field">
                            <label title={t("Qty")}>Q</label>
                            <input
                              type="number"
                              className={`${reviewInputClass(problem.missingQuantity)} ocrp-num`}
                              value={line.quantity || ""}
                              step="0.00000001"
                              min="0"
                              onChange={(event) => handleValueChange(index, "quantity", event.target.value)}
                            />
                          </div>
                          <div className="ocrp-field">
                            <label>{t("Unit Price")}</label>
                            <ComplimentaryPriceField
                              className={`${reviewInputClass(problem.invalidPrice)} ocrp-num`}
                              value={line.unit_price ?? ""}
                              aria-label={`${t("Unit Price")} · ${line.description || index + 1}`}
                              isComplimentary={Boolean(line.is_free_stock)}
                              priceStatus={line.price_status}
                              priceEstimateSource={line.price_estimate_source}
                              onPriceOptions={() => editLinePrice(index)}
                              step="0.00000001"
                              min="0"
                              onChange={(event) => handleValueChange(index, "unit_price", event.target.value)}
                              disabled={saving}
                            />
                            {problem.invalidPrice ? <div className="ocrp-lineNote">{t("Enter a price of zero or greater.")}</div> : null}
                          </div>
                          <div className="ocrp-field">
                            <label>{t("Expiration date")}</label>
                            <ExpirationDateField line={line} item={selectedItem} pendingItem={!selectedItem}
                              disabled={saving || closingDraft || draftBlocked} onChange={(value) => handleValueChange(index, "expiration_date", value)} />
                          </div>
                        </div>
                        <div className="ocrp-total mt-2">{recalcTotal(line.quantity, line.unit_price)} €</div>
                        <div className="ocrp-mobileGrid">
                          <div>
                            <div className="ocrp-mobileLabel">{t("Pending PO")}</div>
                            <div className="ocrp-mobileValue">
                              {assignedTotal > 0
                                ? `Received ${formatQty(assignedTotal)} ${inventoryValues.option?.inventoryLabel || ""}`
                                : pendingTotal > 0
                                  ? `${formatQty(pendingTotal)} pending`
                                  : loadingPendingPo && line.suggested_item_id
                                    ? t("Checking...")
                                    : t("No pending")}
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </>
            )}
          </section>

          {adjustmentHydrationError && <div className="ocrp-feedback ocrp-feedback--error" role="alert">
            {String(languageId).startsWith("es")
              ? "No se puede asociar el descuento guardado con sus artículos. Abre el albarán guardado para revisarlo."
              : String(languageId).startsWith("ca")
                ? "No es pot associar el descompte desat amb els seus articles. Obre l'albarà desat per revisar-lo."
                : "The saved discount cannot be matched to its items. Open the saved delivery note to review it."}
          </div>}
          {adjustmentEditError && <div className="ocrp-feedback ocrp-feedback--error" role="alert">
            {ocrAdjustmentMessage(adjustmentEditError, languageId)}
          </div>}
          <DeliveryNoteAdjustments variant="ocrp" lines={lines} expenses={expenses} languageId={languageId}
            quantityKey="quantity" priceKey="unit_price" getItemId={resolveSuggestedItemId} disabled={saving || adjustmentHydrationError}
            getLineLabel={(line) => {
              const itemId = resolveSuggestedItemId(line);
              if (!itemId) return "";
              return items.find((item) => String(item.id) === itemId)?.name || line.suggested_item?.name || "";
            }}
            onChange={(nextLines, nextExpenses) => {
              adjustmentStateRef.current = { lines: nextLines, expenses: nextExpenses };
              setLines(nextLines); setExpenses(nextExpenses); setAdjustmentEditError("");
            }} />
          </fieldset>
        </main>

        {discountNotice > 0 && <div className="ocrp-discountNotice" role="status" aria-live="polite" aria-atomic="true">
          {ocrAdjustmentMessage("Discount removed after editing items. Apply it again when you finish.", languageId)}
        </div>}
        <footer className="ocrp-reviewFooter">
          <div>
          {!isCreatedOcrImport(importData) && <div className={unresolvedLineCount || unresolvedExpenseCount ? "ocrp-quality ocrp-quality--warning" : "ocrp-quality ocrp-quality--info"}>
            {unresolvedLineCount || unresolvedExpenseCount
              ? t("Fields still needing review: {count}", { count: unresolvedLineCount + unresolvedExpenseCount })
              : t("Ready to create")}
          </div>}
          {draftStatus}
          </div>
          <div className="ocrp-actions">
            <button type="button" className="ocrp-btn" onClick={onClose} disabled={saving || closingDraft}> {t("Close")} </button>
            {isCreatedOcrImport(importData) ? <OcrReopenReviewButton importData={importData} scopedApi={scopedApi} onReopened={onReopened} disabled={saving || closingDraft} /> : <button
              type="button"
              className="ocrp-btn ocrp-btn--success"
              onClick={handleSaveAndCreateDN}
              disabled={!canCreateDeliveryNote || !distributorReady || saving || aiBusy || closingDraft || draftBlocked || Boolean(duplicateRequest)}
              title={
                canCreateDeliveryNote
                  ? t("Create delivery note")
                  : t("Fix highlighted fields before creating the delivery note")
              }
            >
              <FaCheckCircle />
              <span>{t("Create delivery note")}</span>
            </button>}
          </div>
        </footer>
      </div>
      </OriginalDocumentLayout>
      {dateConfirmation.dialog}
      {complimentary.dialog}
      {duplicateRequest && <OcrDuplicateResolution request={duplicateRequest} scopedApi={scopedApi}
        onCancel={() => setDuplicateRequest(null)} onResolved={handleDuplicateResolved} onViewDeliveryNote={onViewDeliveryNote} />}
    </div>
  );
}
